/**
 * ChecksPage.jsx — Conformance Engine UI.
 *
 * Author → Compile → Run flow against the proxy's Conformance Engine
 * (proxy-server/apiServer.js "Conformance Engine" routes, backed by
 * proxy-server/conformance/{compileCheck,runSuite,drivenRun}.js):
 *
 *   1. Author a check in plain English, Compile it (LLM on the proxy) into a
 *      structured Check + validity/rationale.
 *   2. Run — always passive (over selected/session requests), from AgentPanel.
 *      Driving a device/webpage to generate traffic lives inside its Driven toggle.
 *
 * Response field names are bound exactly as returned by the proxy — see
 * src/api/checks.js for the documented shapes. Every field read from the
 * server is optional-chained; a malformed/empty response renders an empty
 * state, never a crash.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';

import {
  estimateScope as apiEstimateScope,
  driverStatus as apiDriverStatus,
  introspectScope as apiIntrospectScope,
} from '../api/checks';
import { getActiveSelection } from '../config/appProfiles';
import { describeAssert, assertSubject, isAbsenceCheck } from '../utils/describeCheck';
import { useTraffic } from '../context/TrafficContext';
import AgentPanel from '../components/AgentPanel';
import { readAdOnlyPreference, writeAdOnlyPreference } from '../utils/adOnlyScope';
import { checkMatchesFamily, familyOfRequestType, ALL_FAMILY, FAMILY_LABELS } from '../utils/vendorFamilies';
import { requestLink } from '../utils/deepLinks';
import SdkVersionsPanel from '../components/SdkVersionsPanel';
import LaunchPage from './LaunchPage';
import RulesPage from './RulesPage';
import IuFinderPage from './IuFinderPage';

// Hex accents for the left-border treatment on non-pass rows (theme-agnostic —
// works regardless of MUI light/dark palette since these are fixed semantic hues).
const STATUS_ACCENT = {
  pass: '#2e7d32',
  warn: '#ed6c02',
  fail: '#d32f2f',
  info: '#0288d1',
  'n/a': '#9e9e9e',
  na: '#9e9e9e',
  // teal/cyan — matches the --rec-* CSS var family added for InspectraNetworkView.
  recommend: '#0e7490',
};
// WP14 (docs/checks-page-ux-spec.md §15) — the ENTIRE visual vocabulary of a
// default result row: "pass then green check, else point to the request
// that failed" (the reviewer's literal ask). One glyph per status, no chip,
// no border-only signal a colorblind reviewer would miss.
const STATUS_GLYPH = {
  pass: '✓',
  warn: '⚠',
  fail: '✗',
  info: 'ℹ',
  'n/a': '–',
  na: '–',
  recommend: '◆',
};

/**
 * filterResultsByStatus(results, status) — WP7 (docs/checks-page-ux-spec.md §9).
 *
 * Pure per-section STATUS facet filter behind the RollupRow pills. Orthogonal
 * to filterResultsByFamily's vendor axis below: that one narrows which ROWS a
 * surviving check's own evidence describes (and stamps `viewMeta`); this one
 * narrows which CHECKS are shown at all, by each result's own already-scored
 * `status`, and never touches a surviving result's evidence/rows/viewMeta —
 * so the two compose for free: run the family filter first (or the layer
 * split), THEN this, and every count downstream still describes exactly
 * what's rendered because both stages only ever subtract whole results, never
 * relabel them.
 *
 * `status` falsy (null — "no pill selected") returns the SAME array reference
 * unmodified, mirroring filterResultsByFamily's "no-op is the identical
 * reference" contract so a caller can tell "unfiltered" from "filtered to
 * everything" for free. 'na' and 'n/a' are treated as one bucket, matching
 * RollupRow's own `rollup['n/a'] ?? rollup.na` merge, so a pill built from
 * either label form matches. Pure, never throws.
 */
export function filterResultsByStatus(results, status) {
  const list = Array.isArray(results) ? results : [];
  if (!status) return list;
  const want = status === 'na' ? 'n/a' : status;
  const filtered = list.filter((r) => {
    const s = r?.status || 'n/a';
    return (s === 'na' ? 'n/a' : s) === want;
  });
  return filtered.length === list.length ? list : filtered;
}

/** Flatten failures/evidence/note into a short human-readable evidence string. */
// Cap for a single stringified expected/got value in the summary cell.
//
// `got` is whatever the check read, and for the whole `*-clean` sweep family
// (baseline-gam-request-clean and friends) that is the ENTIRE issues array —
// JSON.stringify of it runs to thousands of characters. Dumped uncapped into a
// table cell it collapsed the Evidence column to ~one character wide and made
// the row thousands of pixels tall. The full value is still one click away in
// the expandable evidence panel; this cell is a summary.
const EVIDENCE_VALUE_MAX = 160;

export function truncateEvidenceValue(str, max = EVIDENCE_VALUE_MAX) {
  const s = typeof str === 'string' ? str : String(str ?? '');
  if (s.length <= max) return s;
  // Announce the truncation rather than trailing off silently — the remaining
  // character count is the signal that there is more to expand.
  return `${s.slice(0, max)}… (+${s.length - max} chars — expand for full)`;
}

/**
 * formatGotExpectedValue(v) — WP10 (docs/checks-page-ux-spec.md §11).
 *
 * FailureForensics's "Got vs expected" cell used to run every value through
 * JSON.stringify() unconditionally. `got`/`expected` are usually ALREADY a
 * human-readable string — e.g. operators.js's renderValidValue() narration
 * that gotEmbedsExpected() above reasons about, `"<param>=<raw> (INVALID
 * (expected <res.expected>))"` — so re-quoting/escaping it produced runs like
 * `\"320x50|300x250\"` that force a reviewer to mentally unescape JSON while
 * diagnosing a failure, in a paragraph with no wrapping. A string passes
 * through completely unchanged (no re-quoting, no re-escaping); anything
 * else (object/array/number/undefined) — where a plain string cast would
 * lose structure — still goes through JSON.stringify, since that IS the sane
 * rendering for those. Deliberately does NOT call truncateEvidenceValue —
 * that helper already owns length-capping (with its own
 * announce-what-was-cut contract) for the Evidence summary cell elsewhere;
 * duplicating or bypassing it here was explicitly called out as the wrong
 * fix. Pure, never throws.
 */
export function formatGotExpectedValue(v) {
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/**
 * gotEmbedsExpected(got, expected) — WP6 §8c (docs/checks-page-ux-spec.md).
 *
 * Traced origin: for a `validValue` assert, evaluateCheck.js's
 * evaluateAssertOnRow() sets `got: renderValidValue(assert.param, res)`
 * (operators.js) which, on failure, renders `"<param>=<raw> (INVALID
 * (expected <res.expected>))"` — the expectation baked directly into `got`
 * so the "Value read" drill-down cell in EvidenceRowsTable reads standalone
 * without a paired column. The SAME row's top-level `expected` field is set
 * separately (`valid <param> (<res.expected>)`), so `evidenceSummary()`
 * below — which prefixes its own `expected <expected>, got <got>` — ends up
 * printing the same enum/value list twice on one line, e.g.:
 *   expected valid output (one of [vast, ...]), got "output=html (INVALID
 *   (expected one of [vast, ...]))"
 *
 * Fixed here at the display layer rather than in renderValidValue(): `got`
 * also flows into runStore findings, qaRunner/report.js, and MCP payloads,
 * where it is read WITHOUT a sibling `expected` field and needs to stay
 * self-explanatory — narrowing its shape there to fix a page-level
 * duplication would risk those other consumers for a problem they don't have.
 *
 * Detects overlap via the bracketed value list ("[a, b, c]") when `expected`
 * has one — that's the substantive content worth not repeating; the "valid
 * <param> (" wrapper around it is summary-only framing that never appears
 * verbatim inside `got`. Falls back to comparing the whole string. Pure,
 * never throws; returns false (no dedup) on anything it isn't sure about,
 * so the default remains "print both" — no information is ever hidden.
 */
export function gotEmbedsExpected(got, expected) {
  try {
    if (typeof got !== 'string' || typeof expected !== 'string') return false;
    if (!got || !expected) return false;
    const bracketMatch = expected.match(/\[[^\]]*\]/);
    const core = bracketMatch ? bracketMatch[0] : expected;
    return core.length > 0 && got.includes(core);
  } catch {
    return false;
  }
}

/**
 * WP13 §14a (docs/checks-page-ux-spec.md) — this used to fall back to
 * `result.note` when there were no failures. `note` is a SCOPE fact
 * (evaluateCheck.js's buildTypeMismatchNote() / requires-gating messages —
 * see partitionApplicability/notApplicableReasonText above), and the row's
 * own caption (rowScopeNote's `bits` in ResultsTable below) already prints
 * that same `note` verbatim — so the Evidence column and the scope caption
 * rendered the identical string on real n/a rows. Evidence should hold
 * evidence; scope belongs with scope (WP6/WP8) — dropped here, not
 * duplicated, and not lost: it's still shown once, in the caption.
 */
export function evidenceSummary(result) {
  if (!result) return '';
  if (Array.isArray(result.failures) && result.failures.length > 0) {
    const first = result.failures[0];
    const rawGot = first?.got;
    const rawExpected = first?.expected;
    const got = rawGot !== undefined ? truncateEvidenceValue(JSON.stringify(rawGot)) : undefined;
    const expected = rawExpected !== undefined ? truncateEvidenceValue(String(rawExpected)) : undefined;
    // WP6 §8c — `got` sometimes already narrates the expectation it failed
    // (see gotEmbedsExpected above); drop the redundant `expected …` clause
    // rather than printing the same list twice.
    const dropExpected = gotEmbedsExpected(
      typeof rawGot === 'string' ? rawGot : undefined,
      typeof rawExpected === 'string' ? rawExpected : undefined
    );
    const parts = [];
    if (expected && !dropExpected) parts.push(`expected ${expected}`);
    if (got !== undefined) parts.push(`got ${got}`);
    const suffix = result.failures.length > 1 ? ` (+${result.failures.length - 1} more)` : '';
    return parts.length ? `${parts.join(', ')}${suffix}` : `${result.failures.length} failure(s)${suffix}`;
  }
  if (Array.isArray(result.evidence) && result.evidence.length > 0) {
    return `${result.evidenceCount ?? result.evidence.length} row(s) checked`;
  }
  return '';
}

/**
 * failingRequestsSummary(result) -> string|null — WP14 §15 (docs/checks-page-ux-spec.md).
 *
 * The ONE count a failing/warning default row is allowed to state: "N of M
 * requests failed", read straight off `result.matched`/`result.failures`
 * (the same source the pre-WP14 "Matched" column used — see
 * representativeMatched's doc comment above for why that stays a full-run
 * figure rather than a family-filtered one: `filterResultsByFamily` narrows
 * `rows[]` for the evidence drill-down but deliberately leaves `matched`/
 * `failures` untouched as full-run truth). Returns null when there's
 * nothing to report (no matched count, or zero failures — a pass has
 * nothing here). Pure, never throws.
 */
export function failingRequestsSummary(result) {
  try {
    const matched = typeof result?.matched === 'number' ? result.matched : null;
    const failures = Array.isArray(result?.failures) ? result.failures : [];
    if (matched === null || failures.length === 0) return null;
    return `${failures.length} of ${matched} request${matched === 1 ? '' : 's'} failed`;
  } catch {
    return null;
  }
}

/**
 * firstFailingRequestId(result) -> string|null — WP14 §15.
 *
 * Finds a WP0 requestId to deep-link the default row's "view request"
 * action to. Prefers `failureSamples[0].requestId` (runSuite.js's
 * analyzeFailures() already builds this — slimSample() in
 * proxy-server/conformance/failurePatterns.js stamps `requestId` off the
 * failing row), the same field FailureForensics' jumpToSample already
 * trusts. Falls back to scanning `result.rows` (the evidence drill-down —
 * EvidenceRowsTable's jumpToEvidenceRow does the identical `er.requestId`
 * read) for the first row that failed (`ok === false`) and carries one.
 * Returns null — never a falsy placeholder id — when neither source has
 * one, so callers can tell "no link" from "link to nothing" (§15's "degrade
 * to expand-only rather than a dead link"). Pure, never throws.
 */
export function firstFailingRequestId(result) {
  try {
    const fromSample = result?.failureSamples?.[0]?.requestId;
    if (typeof fromSample === 'string' && fromSample) return fromSample;
    const rows = Array.isArray(result?.rows) ? result.rows : [];
    for (const row of rows) {
      if (row && row.ok === false && typeof row.requestId === 'string' && row.requestId) return row.requestId;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * defaultRowFailureNote(result) -> { summary, requestId, link } | null —
 * WP14 §15 (docs/checks-page-ux-spec.md). This return value IS the entire
 * default-row model for a failing/warning check beyond the status glyph and
 * label — "1 of 41 requests failed → [view request]" — and null is the
 * entire default-row model for everything else, including a pass ("a
 * passing check is a tick and its label, nothing else"). Centralizing the
 * decision here (rather than inline in ResultRow's JSX) is what makes the
 * row's content directly testable without mounting the component: a pass
 * (or any status without failures) must return null, a failure with a
 * traceable requestId must return a non-null `link`, and a failure whose
 * evidence never carried one must return `link: null` — never a URL built
 * from an empty id (requestLink() itself already refuses that, but the
 * point of a dedicated null field is a caller can never render a dead
 * anchor by accident). Pure, never throws.
 */
export function defaultRowFailureNote(result) {
  try {
    const status = result?.status;
    if (status !== 'warn' && status !== 'fail') return null;
    const summary = failingRequestsSummary(result);
    if (!summary) return null;
    const requestId = firstFailingRequestId(result);
    return { summary, requestId, link: requestId ? requestLink(requestId) : null };
  } catch {
    return null;
  }
}

/**
 * representativeMatched(results, scopedTotal) — WP6 §8b (docs/checks-page-ux-spec.md).
 *
 * Looks across a family GROUP's results (groupResultsByFamily's output) for
 * how many of them actually narrow below the full run's scopedTotal — i.e.
 * have something worth stating as "N not applicable to this check/family".
 * Returns:
 *   - undefined — no result in the group narrows at all (nothing to
 *     report; the group header stays silent)
 *   - a number  — every narrowing result narrows to the SAME matched count
 *     (the common case: a family's checks all examine the same row
 *     subset), safe to state once in the group header
 *   - null      — narrowing amounts genuinely differ across the group; no
 *     single number is honest to print at the group level, so the caller
 *     falls back to stating it per-row (see rowScopeNote below)
 * Pure, never throws.
 */
export function representativeMatched(results, scopedTotal) {
  try {
    const list = Array.isArray(results) ? results : [];
    const withScope = list.filter(
      (r) => typeof r?.matched === 'number' && typeof scopedTotal === 'number' && scopedTotal > r.matched
    );
    if (withScope.length === 0) return undefined;
    const distinct = new Set(withScope.map((r) => r.matched));
    return distinct.size === 1 ? withScope[0].matched : null;
  } catch {
    return undefined;
  }
}

/**
 * groupScopeNote(results, scopedTotal, familyLabel) — WP6 §8b. The
 * near-constant "34 scoped rows / 31 not applicable" boilerplate that used
 * to repeat on every row (ResultsTable) is lifted out and stated ONCE per
 * family section (GroupedResults' header) — WP2 already partitions results
 * by family, so "a GAM check obviously doesn't apply to Prebid rows" is
 * largely implied by the section itself; this states the concrete numbers
 * for the section rather than every row inside it. Falls back to a
 * scope-varies notice (no fabricated single number) when the group's
 * checks don't share one narrowing amount — see representativeMatched.
 * Pure, never throws; returns null when there is nothing worth saying.
 */
export function groupScopeNote(results, scopedTotal, familyLabel) {
  try {
    const rep = representativeMatched(results, scopedTotal);
    if (rep === undefined) return null;
    const label = familyLabel || 'this family';
    if (rep === null) {
      return `Scope varies by check in this group (of ${scopedTotal} scoped rows) — see each check below`;
    }
    return `Scoped to ${rep} of ${scopedTotal} rows in this run · ${scopedTotal - rep} not applicable to ${label} checks (other request type / context)`;
  } catch {
    return null;
  }
}

/**
 * rowScopeNote(result, scopedTotal, groupReferenceMatched) — WP6 §8a. The
 * ONE count statement a row keeps for itself: whatever the group header (or,
 * outside a grouped render, nothing) does NOT already say. Reconciles what
 * used to be two separate captions — "targeted N of M scoped rows · …" and
 * the WP1 viewMeta line — into a single statement.
 *
 * Two things can be worth stating, never both at once:
 *  1. A family-filter view whose evidence for THIS check is narrower than
 *     what the check actually examined (`result.viewMeta`, WP1 §3 — only
 *     attached by filterResultsByFamily when a chip narrowed the view) —
 *     the invariant that started this whole spec: no number may describe
 *     rows outside the active family without saying so. Always row-level:
 *     viewMeta is per-check (each check's own evidence rows narrow
 *     differently), never a group-wide constant. Gated on
 *     `viewRows !== totalRows` — i.e. the filter actually hid something for
 *     THIS check — so an already-single-family check under its own family's
 *     filter (nothing hidden) prints nothing extra.
 *  2. This check narrows below scopedTotal AND that narrowing amount is NOT
 *     already the group's stated representative (`groupReferenceMatched`) —
 *     i.e. this check's scope genuinely differs from its group's norm, or
 *     there IS no group header to rely on (`groupReferenceMatched`
 *     undefined — e.g. ResultsTable rendered standalone outside
 *     GroupedResults, where nothing else states this fact).
 *
 * Pure, never throws; returns null when the row has nothing left to add.
 */
export function rowScopeNote(result, scopedTotal, groupReferenceMatched) {
  try {
    const matched = typeof result?.matched === 'number' ? result.matched : null;
    if (matched === null) return null;
    const viewMeta = result?.viewMeta || null;
    if (viewMeta && viewMeta.viewRows !== viewMeta.totalRows) {
      const fullRun = typeof scopedTotal === 'number' ? ` of ${scopedTotal} scoped rows` : '';
      return `${viewMeta.viewFound}/${viewMeta.viewRows} shown for ${viewMeta.familyLabel} — full run targeted ${matched}${fullRun}`;
    }
    if (typeof scopedTotal !== 'number' || scopedTotal <= matched) return null;
    if (typeof groupReferenceMatched === 'number' && groupReferenceMatched === matched) return null;
    return `targeted ${matched} of ${scopedTotal} scoped rows · ${scopedTotal - matched} not applicable to this check (other request type / context)`;
  } catch {
    return null;
  }
}

/**
 * partitionApplicability(results) -> { ran, notApplicable } — WP13 §14b
 * (docs/checks-page-ux-spec.md). A check with status 'n/a'/'na' never
 * evaluated anything (evaluateCheck.js returns matched:0 before it ever
 * reaches an assert) — it is not a result someone audits, it is a check
 * that didn't run. Splitting it out here is what lets the caller collapse
 * the n/a slice into one group-level line instead of restating "not
 * applicable" once per check (the reported case: 70 of 87). Pure, never
 * throws; a malformed `results` degrades to two empty arrays.
 */
export function partitionApplicability(results) {
  const list = Array.isArray(results) ? results : [];
  const ran = [];
  const notApplicable = [];
  for (const r of list) {
    const s = r?.status;
    if (s === 'n/a' || s === 'na') notApplicable.push(r);
    else ran.push(r);
  }
  return { ran, notApplicable };
}

// Matches evaluateCheck.js's buildTypeMismatchNote() shape exactly:
// "targets requestType [a, b, c]; <alias descriptions…>; scope contained: …"
// or the adCategory sibling. Searched anywhere in the note (not anchored)
// since alias-description clauses can sit between the target clause and the
// tally — see buildTypeMismatchNote's doc comment in evaluateCheck.js.
const NA_REQUEST_TYPE_RE = /targets requestType \[([^\]]*)\]/;
const NA_AD_CATEGORY_RE = /targets adCategory \[([^\]]*)\]/;

/**
 * notApplicableReasonText(result) -> string — WP13 §14b. Turns one n/a
 * result's `note` into the reviewer-facing reason. When the note is
 * evaluateCheck.js's type-mismatch shape (matched zero rows of a targeted
 * requestType/adCategory), this reduces it to "no A / B / C requests in
 * this session" — the tally suffix ("scope contained: …") is dropped here
 * because it's the SAME string for every check in one run (same session),
 * so restating it per group would just reintroduce the duplication this
 * work package removes; the raw note (tally included) is still what's
 * stored on the result and reachable via the individual row when reasons
 * differ (see groupNotApplicableReason below). Any OTHER n/a note
 * (malformed check, requires-gating, evaluator error, …) is a genuinely
 * different fact — returned unchanged rather than reworded into a
 * traffic-absence claim it didn't make. Pure, never throws.
 */
export function notApplicableReasonText(result) {
  try {
    const note = typeof result?.note === 'string' ? result.note : '';
    if (!note) return 'no reason given';
    const rtMatch = note.match(NA_REQUEST_TYPE_RE);
    const acMatch = note.match(NA_AD_CATEGORY_RE);
    const clauses = [];
    if (rtMatch) {
      const list = rtMatch[1].split(',').map((s) => s.trim()).filter(Boolean);
      if (list.length) clauses.push(`no ${list.join(' / ')} requests`);
    }
    if (acMatch) {
      const list = acMatch[1].split(',').map((s) => s.trim()).filter(Boolean);
      if (list.length) clauses.push(`no ${list.join(' / ')} traffic`);
    }
    if (clauses.length) return `${clauses.join(' and ')} in this session`;
    return note;
  } catch {
    return 'no reason given';
  }
}

/**
 * groupNotApplicableReason(notApplicable) -> { shared, mixed } — WP13 §14b.
 * Decides whether a set of n/a results collapses into ONE group-level line.
 * Compares the raw `note` (not the friendlier text above) across every
 * result — evaluateCheck.js already distinguishes "no target rows of this
 * type" from "requires not satisfied" from "malformed check" etc. at that
 * layer, so an exact match here means they are genuinely the same fact, not
 * a guess. `shared` is the friendly reason text (only computed when every
 * note matches); `mixed: true` means the group must NOT state one cause —
 * callers list the individual checks instead. Pure, never throws.
 */
export function groupNotApplicableReason(notApplicable) {
  try {
    const list = Array.isArray(notApplicable) ? notApplicable : [];
    if (list.length === 0) return { shared: null, mixed: false };
    const notes = list.map((r) => (typeof r?.note === 'string' ? r.note : ''));
    const allSame = notes.every((n) => n === notes[0]);
    if (allSame) return { shared: notApplicableReasonText(list[0]), mixed: false };
    return { shared: null, mixed: true };
  } catch {
    return { shared: null, mixed: true };
  }
}

/**
 * firstDescriptionSentence(text, maxLen) -> string|null — WP12
 * (docs/checks-page-ux-spec.md §13).
 *
 * Baseline check `description`s are engineering changelogs ("blocker B1",
 * "docs/validation-inventory.md §7", file paths) — useful to the author, not
 * to a reviewer who just wants to know what a row checked. This pulls ONLY
 * the first sentence so the changelog tail never leaks into the UI.
 *
 * A naive "split on '. '" breaks two ways real baseline descriptions hit:
 *  - decimal numbers / version lists ("2.0-4.3)." must not split at "2.0")
 *  - abbreviations ("e.g. com.foxnews.android)" must not split at "e.g.")
 * and a case-sensitive "next word must be capitalized" guard breaks a THIRD
 * way ("...targeting. cust_params rides..." — the true boundary is followed
 * by a lowercase technical identifier). So this scans every `[.!?]` run
 * immediately followed by whitespace (or end of string) — which already
 * skips "2.0"/"4.3" since nothing but whitespace ever follows a decimal
 * point mid-number — and additionally skips a candidate when the word
 * immediately before it is a known short abbreviation (e.g./i.e./etc./…),
 * so "e.g. com.foxnews.android" is never mistaken for a boundary either.
 * Pure, never throws.
 */
const SENTENCE_ABBREVIATIONS_RE = /\b(e\.g|i\.e|etc|vs|approx|fig|no|mr|mrs|ms|dr|st|jr|sr)$/i;

export function firstDescriptionSentence(text, maxLen = 220) {
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  try {
    const boundaryRe = /[.!?]+(\s+|$)/g;
    let match;
    let cut = -1;
    while ((match = boundaryRe.exec(trimmed)) !== null) {
      const punctEnd = match.index;
      const before = trimmed.slice(0, punctEnd);
      const lastWordMatch = before.match(/([A-Za-z][A-Za-z.]*)$/);
      const lastWord = lastWordMatch ? lastWordMatch[1] : '';
      if (SENTENCE_ABBREVIATIONS_RE.test(lastWord)) continue; // e.g./i.e./etc. — not a real boundary
      cut = punctEnd + match[0].length;
      break;
    }
    let sentence = cut > -1 ? trimmed.slice(0, cut).trim() : trimmed;
    if (sentence.length > maxLen) sentence = `${sentence.slice(0, maxLen).trim()}…`;
    return sentence || null;
  } catch {
    return trimmed.length > maxLen ? `${trimmed.slice(0, maxLen)}…` : trimmed;
  }
}

/**
 * checkLabel(def, checkId) -> { primary, slug, hasDefinition, descriptionFirstSentence, absence }
 * — WP12 (docs/checks-page-ux-spec.md §13).
 *
 * `def` is the full Check (target/assert/description) resolved from the
 * committed baseline suite's definitions, or undefined/null when no
 * definition matched this checkId. `primary` is the property+rule label a
 * result row should LEAD with — built entirely from describeCheck.js's
 * existing translators (assertSubject/describeAssert), never a second
 * describer. For the dominant `mode: 'each'`/`'all'` shape this reads as
 * "<subject> · <predicate>" (e.g. "validData.vastResponse.version · is one
 * of [2.0, 3.0, 4.0, 4.1, 4.2, 4.3]") by stripping describeAssert()'s known
 * literal "Every matched request: " prefix and re-splitting on the subject
 * substring it already contains — no new op-to-English text is invented.
 * Every OTHER mode (exists/absent/count/ratio/noDuplicates/…) is left as
 * describeAssert()'s full verbatim sentence: those modes carry a quantifier
 * or negation ("NO matched request may have…", "At least one…") that a
 * generic slice could invert into a false statement, so they are not sliced.
 * `checkId` with no definition renders the slug plus an explicit
 * "no definition found" note — never blank, never guessed. Pure, never
 * throws.
 */
export function checkLabel(def, checkId) {
  const slug = (typeof checkId === 'string' && checkId) ? checkId : '(unnamed check)';
  try {
    if (!def || typeof def !== 'object') {
      return {
        primary: `${slug} — no definition found for this check`,
        slug,
        hasDefinition: false,
        descriptionFirstSentence: null,
        absence: false,
      };
    }
    const full = describeAssert(def);
    const mode = typeof def?.assert?.mode === 'string' ? def.assert.mode : 'each';
    let primary = full;
    if ((mode === 'each' || mode === 'all') && typeof full === 'string') {
      const PREFIX = 'Every matched request: ';
      if (full.startsWith(PREFIX)) {
        const tail = full.slice(PREFIX.length).replace(/\.$/, '');
        const subject = assertSubject(def.assert);
        if (subject && tail.startsWith(subject)) {
          const rest = tail.slice(subject.length).trim();
          primary = rest ? `${subject} · ${rest}` : tail;
        } else {
          primary = tail;
        }
      }
    }
    return {
      primary: (typeof primary === 'string' && primary) ? primary : slug,
      slug,
      hasDefinition: true,
      // Prefer the authored `statement` — the rule in ad-tech vocabulary. It
      // exists precisely so the UI stops guessing where the engineering tail
      // of `description` starts; firstDescriptionSentence stays as the fallback
      // for checks compiled at runtime, which carry no statement.
      descriptionFirstSentence: (typeof def.statement === 'string' && def.statement.trim())
        ? def.statement.trim()
        : firstDescriptionSentence(def.description),
      absence: isAbsenceCheck(def),
    };
  } catch {
    return {
      primary: `${slug} — no definition found for this check`,
      slug,
      hasDefinition: false,
      descriptionFirstSentence: null,
      absence: false,
    };
  }
}

// ─── WP15 (docs/checks-page-ux-spec.md §16) — request-oriented JSON pivot ──
//
// THE PIVOT: every result's `rows[]` (WP0's per-request evidence —
// `{ requestId, url, read, ok }`) is regrouped from "one row per CHECK" to
// "one row per REQUEST", keyed by the dotted property path each check
// examined (the SAME translator WP12's checkLabel leads with —
// `assertSubject`, never a second describer). Building the tree by splitting
// that path on `.` is what produces the nested subsections asked for; it
// falls out of paths that already exist rather than being hand-authored.
// Every function below is pure and never throws — see each doc comment.

/** Sentinel: a tree node with this flag is a leaf (`{ ok, read }`), not a subsection. */
const TREE_LEAF_FLAG = '__leaf';

/**
 * Sentinel key: holds a CONTAINER's own leaf verdict when a property path is
 * both a leaf (checked whole, e.g. `cust_params`) and a parent of deeper
 * paths (e.g. `cust_params.hb_pb`) — WP16 §17b (docs/checks-page-ux-spec.md).
 * Replaces the old synthetic `'(value)'` CHILD key, which rendered as a
 * fabricated property name meaning nothing to a reviewer. Stored directly on
 * the container node (not as an enumerable "normal" child) so
 * `PropertyTreeNode` can render it as the container line's own verdict and
 * skip it when walking children.
 */
const TREE_SELF_LEAF_KEY = '__self';

/**
 * checkDefForResult(result, checkDefsById) -> object|undefined — shared
 * definition lookup used by both propertyPathForResult (the property path)
 * and pivotResultsByRequest (the absence flag, WP16 §17a). Kept as a single
 * spot rather than duplicating the `checkDefsById.get` guard everywhere.
 */
function checkDefForResult(result, checkDefsById) {
  try {
    const checkId = result?.checkId;
    return checkDefsById && typeof checkDefsById.get === 'function'
      ? checkDefsById.get(checkId)
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * propertyPathForResult(result, checkDefsById) -> string|null — WP15 §16.
 * The dotted property path a check examined, e.g. `cust_params.appv`,
 * `vastResponse.version`, `ppid` — built from `assertSubject` off the same
 * check DEFINITION checkLabel resolves (`checkDefsById` is only wired up for
 * the committed baseline suite today — see the two `<LayeredResults>` call
 * sites in this file). Falls back to the raw `checkId` when no definition is
 * available so a result's rows never silently drop out of the pivot; they
 * just can't be split into subsections without a real property path (a flat
 * leaf keyed by the slug is still better than losing the row).
 */
export function propertyPathForResult(result, checkDefsById) {
  try {
    const checkId = result?.checkId;
    const def = checkDefForResult(result, checkDefsById);
    if (def && typeof def === 'object' && def.assert) {
      const subject = assertSubject(def.assert);
      if (typeof subject === 'string' && subject.trim()) return subject.trim();
    }
    return typeof checkId === 'string' && checkId ? checkId : null;
  } catch {
    return null;
  }
}

/**
 * insertPropertyLeaf(tree, path, leaf) — mutates `tree` in place, building
 * the nested-subsection structure directly from splitting a dotted property
 * path on `.` (`cust_params.appv` -> `{ cust_params: { appv: <leaf> } }`).
 * A path segment that collides with an already-placed leaf (one check reads
 * a bare value at some key, another reads a deeper path through that same
 * key) does not clobber either reading: the earlier leaf survives under the
 * `TREE_SELF_LEAF_KEY` sentinel alongside its new children rather than being
 * silently dropped — this page's whole spec history (WP4/WP6/WP-no-silent-
 * caps) is a standing rule against quietly discarding data. WP16 §17b
 * (docs/checks-page-ux-spec.md): that sentinel used to be the enumerable
 * child key `'(value)'`, which rendered in the UI as a fabricated property
 * name; `PropertyTreeNode` now reads it back out and renders it as the
 * CONTAINER's own verdict instead of a child row.
 */
function insertPropertyLeaf(tree, path, leaf) {
  const parts = String(path).split('.').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return;
  let node = tree;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const existing = node[key];
    if (!existing || typeof existing !== 'object' || existing[TREE_LEAF_FLAG]) {
      const next = {};
      if (existing && existing[TREE_LEAF_FLAG]) next[TREE_SELF_LEAF_KEY] = existing;
      node[key] = next;
    }
    node = node[key];
  }
  const leafKey = parts[parts.length - 1];
  const existing = node[leafKey];
  if (existing && typeof existing === 'object' && !existing[TREE_LEAF_FLAG]) {
    existing[TREE_SELF_LEAF_KEY] = leaf;
  } else {
    node[leafKey] = leaf;
  }
}

/**
 * sortTreeKeysDeep(node) -> new tree with every level's keys sorted
 * alphabetically — WP15 §16: "Sort keys alphabetically at EVERY level."
 * Leaves (`{ __leaf: true, ... }`) pass through unchanged (a leaf has no
 * subsections to sort). Pure, never throws; malformed input is returned
 * as-is rather than crashing.
 */
export function sortTreeKeysDeep(node) {
  try {
    if (!node || typeof node !== 'object') return node;
    if (node[TREE_LEAF_FLAG] === true) return node;
    const sorted = {};
    for (const key of Object.keys(node).sort((a, b) => a.localeCompare(b))) {
      sorted[key] = sortTreeKeysDeep(node[key]);
    }
    return sorted;
  } catch {
    return node;
  }
}

/**
 * pivotResultsByRequest(results, checkDefsById) -> Array<{ requestId, url,
 * tree, hasFailure }> — WP15 §16 (docs/checks-page-ux-spec.md). THE PIVOT
 * itself: groups every result's evidence rows by `requestId`, keys each one
 * by the property path its check examined (propertyPathForResult), and
 * builds the nested/alphabetized tree (insertPropertyLeaf + sortTreeKeysDeep)
 * a JSON-object view can render directly. A request that no check's `rows[]`
 * ever named does not appear in the output — "a request touched by zero
 * checks does not appear" (WP15 acceptance). Array order is "first request
 * id encountered while scanning `results`" — stable for a given input, not
 * re-sorted here; choosing WHICH entry a viewer sees first is
 * pickDefaultRequestId's job below, kept separate from this data shape.
 * Pure, never throws; garbage input yields `[]`.
 */
export function pivotResultsByRequest(results, checkDefsById) {
  try {
    const list = Array.isArray(results) ? results : [];
    const order = [];
    const byRequest = new Map();
    for (const r of list) {
      if (!r || typeof r !== 'object') continue;
      const path = propertyPathForResult(r, checkDefsById);
      if (!path) continue;
      // WP16 §17a (docs/checks-page-ux-spec.md) — carry the SAME absence
      // detector the rest of the page already uses (describeCheck.js's
      // isAbsenceCheck) onto every leaf this result produces, so a passing
      // absence check can render as "absent, as required" instead of
      // formatLeafRead's old default "(present)" — which was the exact
      // opposite of what happened on a leak indicator.
      const absence = isAbsenceCheck(checkDefForResult(r, checkDefsById));
      // WP16 §17c — best-effort expected-value lookup for a FAILING row,
      // matched by id (falling back to url) against `result.failures[]`.
      // Reuses the same expectation SOURCE the summary's evidenceSummary()
      // already reads (`failures[].expected`) rather than deriving a second
      // one, keyed exactly the way runStore.js's summarizeCheckResult()
      // already keys it — evidence rows and failures are built from the
      // same contextRows in evaluateCheck.js, so id/url line up.
      const expectedByKey = new Map();
      const failuresList = Array.isArray(r.failures) ? r.failures : [];
      for (const f of failuresList) {
        const frow = f && f.row;
        const fkey = frow && frow.id != null ? `id:${frow.id}` : (frow && frow.url != null ? `url:${frow.url}` : null);
        if (fkey && !expectedByKey.has(fkey)) expectedByKey.set(fkey, f.expected);
      }
      const rows = Array.isArray(r.rows) ? r.rows : [];
      for (const row of rows) {
        if (!row || typeof row !== 'object') continue;
        const requestId = row.requestId;
        if (!requestId) continue;
        let entry = byRequest.get(requestId);
        if (!entry) {
          entry = { requestId, url: (typeof row.url === 'string' && row.url) || '', tree: {}, hasFailure: false };
          byRequest.set(requestId, entry);
          order.push(requestId);
        } else if (!entry.url && typeof row.url === 'string' && row.url) {
          entry.url = row.url;
        }
        const ok = row.ok === true;
        if (!ok) entry.hasFailure = true;
        const rowKey = row.id != null ? `id:${row.id}` : (row.url != null ? `url:${row.url}` : null);
        const expected = (!ok && rowKey && expectedByKey.has(rowKey)) ? expectedByKey.get(rowKey) : undefined;
        insertPropertyLeaf(entry.tree, path, { [TREE_LEAF_FLAG]: true, ok, read: row.read, absence, expected });
      }
    }
    return order.map((id) => {
      const entry = byRequest.get(id);
      return { ...entry, tree: sortTreeKeysDeep(entry.tree) };
    });
  } catch {
    return [];
  }
}

/**
 * pickDefaultRequestId(pivoted) -> string|null — WP15 §16's "open question":
 * which request does the JSON view show first? Prefers the first request (in
 * pivotResultsByRequest's order) carrying at least one failing property — the
 * reviewer's own framing for a check row is "pass then green check, else
 * point to the request that failed" (WP14), and this page's whole redesign
 * history (WP6/WP7/WP13) argues against defaulting to a clean/quiet view
 * when a broken one exists to show instead. Falls back to the first request
 * in the list when nothing failed (an all-pass session — still worth seeing
 * one request's shape). Returns null for an empty pivot. Pure, never throws.
 */
export function pickDefaultRequestId(pivoted) {
  try {
    const list = Array.isArray(pivoted) ? pivoted : [];
    const failing = list.find((e) => e && e.hasFailure);
    if (failing) return failing.requestId;
    return list.length ? list[0].requestId : null;
  } catch {
    return null;
  }
}

/**
 * formatLeafRead(leaf) -> string — WP15 §16, corrected by WP16 §17
 * (docs/checks-page-ux-spec.md). A leaf shows the verdict glyph and "the
 * value actually read" (`read` — what was on the wire, currently buried
 * behind the evidence-table expander; "the single most useful thing on this
 * page"). When a check FAILED and `read` is empty/absent, states what that
 * means (`missing`) rather than rendering a blank string — an absent value
 * IS the finding on a failing leaf. Pure, never throws.
 *
 * WP16 §17a — ABSENCE checks (`leaf.absence`, threaded from
 * `describeCheck.js`'s `isAbsenceCheck` by pivotResultsByRequest) invert the
 * old present/missing framing entirely: these assert "no matched request may
 * carry this" (leak indicators, forbidden params), so `ok: true` means the
 * thing was legitimately never found — the OLD code fell through to the
 * generic no-read branch and printed `'(present)'`, i.e. the exact opposite
 * of what happened, on a privacy-leak glyph. A FAILING absence leaf means
 * the thing WAS found, so it says so and states the offending value.
 *
 * WP16 §17c — a FAILING leaf states its expectation, not only what was
 * read: `leaf.expected` (pivotResultsByRequest's best-effort lookup against
 * `result.failures[]`, the SAME expectation source evidenceSummary() already
 * renders in the summary below) is appended as `(expected …)`. Reuses
 * `gotEmbedsExpected` — already defined above for exactly this purpose — to
 * skip the suffix when the read value already narrates the expectation
 * inline (e.g. a `validValue` assert's `"<param>=<raw> (INVALID (expected
 * …))"` rendering), so the expectation is never printed twice.
 */
export function formatLeafRead(leaf) {
  try {
    const ok = leaf?.ok === true;
    const absence = leaf?.absence === true;
    const read = leaf?.read;
    const hasRead = !(read === undefined || read === null || read === '');
    const readStr = hasRead
      ? (() => { try { return JSON.stringify(read); } catch { return String(read); } })()
      : null;

    if (absence) {
      if (ok) return '(absent, as required)';
      return hasRead ? `found: ${readStr}` : 'found (present, expected absent)';
    }

    if (ok) {
      return hasRead ? readStr : '(present)';
    }

    // Failing, non-absence leaf: state the expectation — the read value
    // alone can't explain a red X on an otherwise-reasonable-looking value.
    const gotStr = hasRead ? readStr : 'missing';
    const rawExpected = leaf?.expected;
    const hasExpected = !(rawExpected === undefined || rawExpected === null || rawExpected === '');
    if (!hasExpected) return gotStr;
    const expectedStr = typeof rawExpected === 'string'
      ? rawExpected
      : (() => { try { return JSON.stringify(rawExpected); } catch { return String(rawExpected); } })();
    // Same dedup gotEmbedsExpected already performs for the summary line —
    // a `validValue` assert's `got` sometimes narrates the expectation
    // inline already (WP6 §8c); don't restate it here.
    const dropExpected = gotEmbedsExpected(
      typeof read === 'string' ? read : undefined,
      typeof rawExpected === 'string' ? rawExpected : undefined
    );
    if (dropExpected) return gotStr;
    return `${gotStr} (expected ${expectedStr})`;
  } catch {
    return 'missing';
  }
}

// ─── WP17 (docs/checks-page-ux-spec.md §18) — family-card property tree ───
//
// THE AGGREGATION PIVOT: WP15's pivot above groups a family's results by
// REQUEST (one leaf per (check, request) pair). This one groups the SAME
// family's results by PROPERTY, aggregated ACROSS every request the check
// examined — one leaf per CHECK, carrying tallies instead of a single
// read/ok pair. Reuses the exact same tree-shape primitives (TREE_LEAF_FLAG/
// TREE_SELF_LEAF_KEY, insertPropertyLeaf, sortTreeKeysDeep,
// propertyPathForResult, checkDefForResult) so both pivots produce the
// identical alphabetical/nested JSON shape a reviewer already recognizes.

/**
 * SESSION_LEVEL_MODES — same vocabulary as the local const of the same name
 * in frontend/src/utils/exportCheckForBaseline.js (that file's own
 * baseline-export gate); this is the THIRD consumer of the same
 * each/all/exists/absent/count (property of one request) vs.
 * noDuplicates/minInterval/uniform/ratio (fact about a SET of matched
 * requests) split. Redefined locally rather than imported — WP17 is scoped
 * to this file only. These four modes never appear as a key inside a family
 * card's property object; they render in the CROSS-REQUEST block beneath it.
 */
export const SESSION_LEVEL_MODES = ['noDuplicates', 'minInterval', 'uniform', 'ratio'];

/**
 * leafRuleText(def, checkId) -> string — the plain-language rule text beside
 * a property key, e.g. `an: present`. Reuses `checkLabel` (WP12) verbatim —
 * no new English is generated here — and, for the common each/all shape
 * where `checkLabel` already returns `"<subject> · <predicate>"`, strips the
 * repeated subject back off (the tree already shows the subject as the
 * property key one column to the left; restating it in the value would be
 * the same "fabricated redundancy" WP16 §17b already fixed once for the
 * per-request pivot). Every other mode's full sentence is left untouched —
 * same reasoning as checkLabel's own doc comment: a generic slice could
 * invert a negated/quantified sentence into something false. Pure, never
 * throws.
 */
function leafRuleText(def, checkId) {
  try {
    const label = checkLabel(def, checkId);
    if (!label.hasDefinition) return label.primary;
    const subject = def && def.assert ? assertSubject(def.assert) : null;
    const prefix = subject ? `${subject} · ` : null;
    if (prefix && typeof label.primary === 'string' && label.primary.startsWith(prefix)) {
      return label.primary.slice(prefix.length);
    }
    return label.primary;
  } catch {
    return typeof checkId === 'string' ? checkId : '(unnamed check)';
  }
}

/**
 * aggregateFamilyByProperty(results, checkDefsById) -> { degraded, tree,
 * crossRequest, resultsByCheckId } — WP17 §18. THE aggregation pivot: one
 * leaf per CHECK (not per (check, request) pair like WP15's pivot),
 * `{ __leaf: true, checkId, status, passCount, matchedCount, failedCount,
 * firstFailingRequestId }`, keyed by the dotted property path the check
 * examined (propertyPathForResult, unchanged) and nested/alpha-sorted the
 * same way (insertPropertyLeaf + sortTreeKeysDeep, unchanged).
 *
 * Tallies read `result.matched` / `result.failures.length` — NEVER the
 * evidence-capped `result.rows[]` (evaluateCheck.js's `finalizeEvidenceRows`
 * truncates `rows[]` at EVIDENCE_ROW_CAP but never truncates `failures[]` —
 * the same convention `failingRequestsSummary`/the WP14 matched-tally caption
 * already rely on), so a card's counts always reconcile against the
 * existing per-check summary line.
 *
 * A `status` of 'n/a'/'na' is excluded entirely — WP13's existing N/A
 * collapse already gives it a home as one line per family
 * (`partitionApplicability`/`groupNotApplicableReason`, unchanged); this
 * function only ever sees whatever slice its caller decided is worth
 * aggregating (callers pass the "ran" half of that same partition).
 *
 * `degraded: true` — no property paths are resolvable at all
 * (`checkDefsById` absent/empty), so there is nothing to key a tree by; the
 * caller falls back to the flat WP14 `ResultsTable` rows instead of
 * rendering an empty or misleading object.
 *
 * SESSION_LEVEL_MODES (noDuplicates/minInterval/uniform/ratio) route to
 * `crossRequest` and are NEVER inserted into `tree` — the property/
 * cross-request split is principled, not heuristic: every other mode
 * asserts about ONE property of ONE request (belongs in the object); these
 * four assert about a SET of matched requests (belong in their own block).
 *
 * Pure, never throws; garbage input degrades to an empty, non-degraded
 * result rather than an exception — a family card renders empty, not broken.
 */
export function aggregateFamilyByProperty(results, checkDefsById) {
  try {
    const list = Array.isArray(results) ? results : [];
    const hasDefs = !!(checkDefsById && typeof checkDefsById.get === 'function' && checkDefsById.size > 0);
    const resultsByCheckId = new Map();
    for (const r of list) {
      if (r && typeof r.checkId === 'string') resultsByCheckId.set(r.checkId, r);
    }
    if (!hasDefs) {
      return { degraded: true, tree: {}, crossRequest: [], resultsByCheckId };
    }
    const tree = {};
    const crossRequest = [];
    for (const r of list) {
      if (!r || typeof r !== 'object') continue;
      const status = r.status;
      if (status === 'n/a' || status === 'na') continue;
      const def = checkDefForResult(r, checkDefsById);
      const mode = def && def.assert ? def.assert.mode : undefined;
      const matchedCount = typeof r.matched === 'number' ? r.matched : 0;
      const failedCount = Array.isArray(r.failures) ? r.failures.length : 0;
      const passCount = Math.max(0, matchedCount - failedCount);
      const leaf = {
        [TREE_LEAF_FLAG]: true,
        checkId: r.checkId,
        status,
        passCount,
        matchedCount,
        failedCount,
        firstFailingRequestId: firstFailingRequestId(r),
      };
      if (SESSION_LEVEL_MODES.includes(mode)) {
        crossRequest.push(leaf);
        continue;
      }
      const path = propertyPathForResult(r, checkDefsById);
      if (!path) continue;
      insertPropertyLeaf(tree, path, leaf);
    }
    return { degraded: false, tree: sortTreeKeysDeep(tree), crossRequest, resultsByCheckId };
  } catch {
    return { degraded: false, tree: {}, crossRequest: [], resultsByCheckId: new Map() };
  }
}

/**
 * formatAggregateTally(result, leaf) -> string — "94/94" for a clean leaf;
 * otherwise reuses `failingRequestsSummary` (WP14) verbatim so the SAME "N
 * of M requests failed" phrasing appears here as everywhere else on the
 * page rather than a second tally wording invented for this card. Falls
 * back to a locally-built count only if `failingRequestsSummary` can't
 * resolve one from `result` (e.g. `result` is missing because the caller
 * only has the leaf) — never throws.
 */
function formatAggregateTally(result, leaf) {
  try {
    if (!leaf || leaf.failedCount === 0) {
      const m = (leaf && leaf.matchedCount) || 0;
      return `${m}/${m}`;
    }
    return failingRequestsSummary(result) || `${leaf.failedCount} of ${leaf.matchedCount} failed`;
  } catch {
    return '';
  }
}

/**
 * aggregateFailureDrilldown(result) -> string|null — the "host · time ·
 * offending value ← expectation" line under a failing leaf (§18's literal
 * mockup format). Reuses `result.failureSamples[0]` — the SAME source
 * `FailureForensics`/`defaultRowFailureNote` already render, built
 * server-side by runSuite.js's `analyzeFailures()` — rather than re-deriving
 * host/time/value here. `gotEmbedsExpected` (WP6 §8c/WP16 §17c) is reused
 * verbatim so the expectation is never printed twice. Pure, never throws.
 */
function aggregateFailureDrilldown(result) {
  try {
    const sample = result && Array.isArray(result.failureSamples) ? result.failureSamples[0] : null;
    if (!sample) return null;
    const host = sample.host || null;
    const time = formatSampleTime(sample.ts) || null;
    const where = sample.iu || sample.path || null;
    const rawGot = sample.got;
    const rawExpected = sample.expected;
    const got = rawGot !== undefined
      ? (typeof rawGot === 'string' ? rawGot : (() => { try { return JSON.stringify(rawGot); } catch { return String(rawGot); } })())
      : null;
    const expected = rawExpected !== undefined
      ? (typeof rawExpected === 'string' ? rawExpected : (() => { try { return JSON.stringify(rawExpected); } catch { return String(rawExpected); } })())
      : null;
    const dropExpected = gotEmbedsExpected(
      typeof rawGot === 'string' ? rawGot : undefined,
      typeof rawExpected === 'string' ? rawExpected : undefined
    );
    const head = [host, time].filter(Boolean).join(' · ');
    let line = head;
    if (where && got) line += `${line ? ' · ' : ''}${where}: ${got}`;
    else if (got) line += `${line ? ' · ' : ''}${got}`;
    if (expected && !dropExpected) line += ` ← ${expected}`;
    return line || null;
  } catch {
    return null;
  }
}

// ─── WP17 (docs/checks-page-ux-spec.md §18) — container encoding-integrity join ──
//
// The committed hygiene check (proxy-server/hygiene/hygieneChecks.js,
// shipped in 4a3e8d7) whose results this file joins onto container property
// lines (cust_params, FreeWheel's ss_/_fw_ family, any other nested-
// delimiter param — proxy-server/hygiene/nestedDelimiters.js is the proxy's
// own source of truth; nothing here hardcodes a field-name list, it only
// reads whatever `field` a failing issue actually names).
const ENCODING_CHECK_ID = 'baseline-url-encoding-clean';
const ENCODING_ISSUE_CODES = new Set([
  'double_encoded',
  'under_encoded_nested_value',
  'truncated_value',
  'unencoded_nested_url',
]);
// under_encoded_nested_value's message names the leaked sibling verbatim —
// hygieneChecks.js's checkUnderEncodedNestedValue(): `... the very next
// query param "${leaked}" isn't a recognized top-level param`.
const LEAKED_KEY_RE = /next query param "([^"]+)"/i;

/**
 * issuesForFailureRow(failure, rowsByRequestId) -> { issues, requestId } —
 * `failure.row` (evaluateCheck.js's per-failure context row) is the full,
 * unstripped session row all the way to the wire: runSuite.js strips only
 * `_targetRows` off a RESULT (never a failure's `row`), apiServer.js's
 * `POST /proxy/checks/run` handler adds no further stripping, and
 * sessionContext.js's `buildSessionContext` sets `rows: safeRows` — the
 * literal array reference, not a clone — so `failure.row.issues[]` is
 * normally usable directly. Falls back to `rowsByRequestId` (built from the
 * currently loaded session) only when the embedded row lacks `.issues`
 * (e.g. a slimmed/cached run-history payload), so this degrades rather than
 * silently under-reporting. Pure, never throws.
 */
function issuesForFailureRow(failure, rowsByRequestId) {
  try {
    const row = failure && failure.row;
    const requestId = (row && row.requestId) || null;
    if (row && Array.isArray(row.issues) && row.issues.length) {
      return { issues: row.issues, requestId };
    }
    if (requestId && rowsByRequestId) {
      const raw = typeof rowsByRequestId.get === 'function'
        ? rowsByRequestId.get(requestId)
        : rowsByRequestId[requestId];
      if (raw && Array.isArray(raw.issues)) return { issues: raw.issues, requestId };
    }
    return { issues: [], requestId };
  } catch {
    return { issues: [], requestId: null };
  }
}

/**
 * buildEncodingIntegrityByField(results, rowsByRequestId) -> { checked,
 * allClean, byField: Map<field, { status, failedCount, leakedKeys: string[],
 * firstFailingRequestId, code }> } — WP17 §18. Locates the ONE committed
 * `baseline-url-encoding-clean` result among `results` (severity warn,
 * absence-mode over `issues` — proxy-server/conformance/suites/
 * adtech-baseline.checks.json) and buckets its failures by `issue.field`.
 *
 * `checked: false` — the check wasn't run (older/non-baseline suite) or came
 * back n/a (nothing in scope) — attach NO verdict anywhere; silence beats a
 * false claim.
 * `allClean: true` — the check ran and passed outright (zero failures) —
 * every container in every family may show a clean verdict.
 * `byField` — populated ONLY for fields that literally appear as a failing
 * issue's `field`; a container never gets a verdict it can't be evidenced
 * for either way. Computed ONCE over the FULL (unfiltered-by-family)
 * `results` — this one check's evidence spans many request types, so it is
 * intentionally NOT recomputed per family (a per-family computation would
 * only ever see it under whichever ONE family `familyOfResult` attributed it
 * to, and every OTHER family's matching containers would silently miss it).
 *
 * Pure, never throws; garbage input -> `{checked: false, allClean: false,
 * byField: new Map()}`.
 */
export function buildEncodingIntegrityByField(results, rowsByRequestId) {
  try {
    const list = Array.isArray(results) ? results : [];
    const encodingResult = list.find((r) => r && r.checkId === ENCODING_CHECK_ID);
    if (!encodingResult || encodingResult.status === 'n/a' || encodingResult.status === 'na') {
      return { checked: false, allClean: false, byField: new Map() };
    }
    const failures = Array.isArray(encodingResult.failures) ? encodingResult.failures : [];
    if (failures.length === 0) {
      return { checked: true, allClean: true, byField: new Map() };
    }
    const severity = encodingResult.severity || 'warn';
    const byField = new Map();
    for (const f of failures) {
      const { issues, requestId } = issuesForFailureRow(f, rowsByRequestId);
      for (const issue of issues) {
        if (!issue || !ENCODING_ISSUE_CODES.has(issue.code)) continue;
        const field = issue.field;
        if (typeof field !== 'string' || !field) continue;
        let entry = byField.get(field);
        if (!entry) {
          entry = { status: severity, failedCount: 0, leakedKeys: new Set(), firstFailingRequestId: null, code: issue.code };
          byField.set(field, entry);
        }
        entry.failedCount += 1;
        if (!entry.firstFailingRequestId && requestId) entry.firstFailingRequestId = requestId;
        if (issue.code === 'under_encoded_nested_value') {
          const text = (typeof issue.message === 'string' && issue.message)
            || (typeof issue.note === 'string' && issue.note) || '';
          const m = LEAKED_KEY_RE.exec(text);
          if (m && m[1]) entry.leakedKeys.add(m[1]);
        }
      }
    }
    const finalized = new Map();
    for (const [field, entry] of byField) {
      finalized.set(field, { ...entry, leakedKeys: Array.from(entry.leakedKeys).sort() });
    }
    return { checked: true, allClean: false, byField: finalized };
  } catch {
    return { checked: false, allClean: false, byField: new Map() };
  }
}

/**
 * attachEncodingIntegrity(tree, encodingInfo) -> tree (mutated in place, also
 * returned) — WP17 §18. Walks every CONTAINER node (a subsection with
 * children — never a plain leaf, see TREE_LEAF_FLAG) and stamps an
 * `encoding` verdict onto its `TREE_SELF_LEAF_KEY` slot:
 *   - `encodingInfo.byField` has this key -> `{ clean: false, failedCount,
 *     leakedKeys, firstFailingRequestId, code }` (the field-specific
 *     verdict).
 *   - no entry for this key AND `encodingInfo.allClean` -> `{ clean: true }`.
 *   - neither (check wasn't run/came back n/a, or ran with only OTHER
 *     fields failing) -> no `encoding` key at all; the container renders
 *     exactly as it did before WP17.
 * A container's key is whatever `insertPropertyLeaf` already keyed it as
 * (the dotted-path's last segment, e.g. `cust_params`) — this never
 * hardcodes a field name, it only asks "does `encodingInfo.byField` know
 * this key". Recurses into every level. Pure (only mutates the tree object
 * it's handed), never throws.
 */
export function attachEncodingIntegrity(tree, encodingInfo) {
  try {
    if (!tree || typeof tree !== 'object') return tree;
    if (!encodingInfo || !encodingInfo.checked) return tree;
    const byField = encodingInfo.byField instanceof Map ? encodingInfo.byField : new Map();
    for (const key of Object.keys(tree)) {
      if (key === TREE_SELF_LEAF_KEY) continue;
      const node = tree[key];
      if (!node || typeof node !== 'object') continue;
      if (node[TREE_LEAF_FLAG] === true) continue; // plain leaf, not a container — spec scopes this to container lines
      const verdict = byField.has(key)
        ? { clean: false, ...byField.get(key) }
        : (encodingInfo.allClean ? { clean: true } : null);
      if (verdict) {
        const selfLeaf = node[TREE_SELF_LEAF_KEY];
        node[TREE_SELF_LEAF_KEY] = selfLeaf ? { ...selfLeaf, encoding: verdict } : { encoding: verdict };
      }
      attachEncodingIntegrity(node, encodingInfo);
    }
    return tree;
  } catch {
    return tree;
  }
}

// ─── WP18 (docs/checks-page-ux-spec.md §19) — collapsible container nodes ──
//
// THE NON-NEGOTIABLE RULE: collapsing a container must never make a failing
// subtree look clean. Every helper below is pure and operates on the SAME
// tree shape aggregateFamilyByProperty/attachEncodingIntegrity already
// build (TREE_LEAF_FLAG leaves, TREE_SELF_LEAF_KEY sentinel, dotted paths)
// so the render layer never has to re-derive status from anywhere else.

// Same fail > warn > recommend > info/pass ordering WP1 already established
// (recommend is advisory, never folded into fail/warn/pass — see RollupRow's
// doc comment). An unrecognized/unknown status string falls back to the FAIL
// rank rather than pass — never silently under-count a status this file
// doesn't know about yet.
const STATUS_RANK = { fail: 4, warn: 3, recommend: 2, info: 1, pass: 0 };

/**
 * leafStatusKey(leaf) -> status string, 'n/a' merged from either 'na' or
 * 'n/a' spelling (RollupRow does the same merge). Mirrors the SAME field
 * (`result.status`) GroupedResults' own `rollupOf`/`defaultOpen` already
 * roll up on — this is that logic's tree-shaped sibling, not a second one.
 */
function leafStatusKey(leaf) {
  const s = leaf && leaf.status;
  if (s === 'na') return 'n/a';
  return typeof s === 'string' && s ? s : 'n/a';
}

/**
 * worstStatusInNode(node) -> status string — WP18 §19. Recursively finds the
 * worst status among every check leaf beneath `node` (a leaf itself, or any
 * container at any depth), fail > warn > recommend > info > pass. 'n/a' is
 * ignored whenever anything else is present (an n/a leaf sitting next to a
 * failing one must never drag the rollup down to "nothing to report"); when
 * EVERY leaf beneath `node` is n/a (or there are no leaves at all — an empty
 * container), the whole node is 'n/a'. The `TREE_SELF_LEAF_KEY` sentinel is
 * only counted when it's a real check leaf (collision case, WP16 §17b) —
 * an encoding-only sentinel (`{ encoding: {...} }`, no TREE_LEAF_FLAG,
 * attachEncodingIntegrity's doing) carries no status of its own and is
 * skipped here; ContainerEncodingLine renders it separately and
 * unconditionally regardless of collapse state (WP18's other rule). Pure,
 * never throws; garbage input degrades to 'n/a' rather than crashing.
 */
export function worstStatusInNode(node) {
  try {
    if (node && typeof node === 'object' && node[TREE_LEAF_FLAG] === true) {
      return leafStatusKey(node);
    }
    if (!node || typeof node !== 'object') return 'n/a';
    let worst = null;
    let worstRank = -1;
    let sawNonNa = false;
    for (const key of Object.keys(node)) {
      const child = node[key];
      if (!child || typeof child !== 'object') continue;
      if (key === TREE_SELF_LEAF_KEY && child[TREE_LEAF_FLAG] !== true) continue;
      const s = worstStatusInNode(child);
      if (s === 'n/a') continue;
      sawNonNa = true;
      const rank = STATUS_RANK[s] ?? STATUS_RANK.fail;
      if (rank > worstRank) { worstRank = rank; worst = s; }
    }
    return sawNonNa ? worst : 'n/a';
  } catch {
    return 'n/a';
  }
}

/**
 * nodeNeedsExpand(node) -> boolean — WP18 §19's default-state policy, "any
 * container with fail/warn anywhere beneath starts expanded; all-clean
 * containers start collapsed" — same rule WP2 already applies to family
 * cards (`GroupedResults`' `defaultOpen = rollup.fail > 0 || rollup.warn >
 * 0`), reused here at every tree depth instead of re-invented. An
 * unrecognized status rides worstStatusInNode's fail-rank fallback, so it
 * expands too — never hide something this file can't classify.
 */
function nodeNeedsExpand(node) {
  const worst = worstStatusInNode(node);
  return (STATUS_RANK[worst] ?? STATUS_RANK.fail) >= STATUS_RANK.warn;
}

/**
 * containerSummary(node) -> { childCount, failingCount, worst } — WP18 §19's
 * collapsed-line content: "cust_params ▸ 4 keys · 1 failing". `childCount`
 * is `node`'s own direct property count (the `TREE_SELF_LEAF_KEY` sentinel
 * excluded — it's not a rendered child key); `failingCount` is how many of
 * those DIRECT children have a fail/warn somewhere in their own subtree
 * (nodeNeedsExpand, so a deeply-nested failure still surfaces as "1 failing"
 * on its immediate parent, never just on the leaf itself); `worst` is this
 * node's own worstStatusInNode, for the collapsed line's glyph. Pure, never
 * throws; garbage input yields an all-zero/n-a summary rather than crashing.
 */
export function containerSummary(node) {
  try {
    if (!node || typeof node !== 'object') return { childCount: 0, failingCount: 0, worst: 'n/a' };
    const keys = Object.keys(node).filter((k) => k !== TREE_SELF_LEAF_KEY);
    let failingCount = 0;
    for (const k of keys) {
      if (nodeNeedsExpand(node[k])) failingCount += 1;
    }
    return { childCount: keys.length, failingCount, worst: worstStatusInNode(node) };
  } catch {
    return { childCount: 0, failingCount: 0, worst: 'n/a' };
  }
}

/**
 * defaultExpandedPaths(tree, opts) -> Set<string> — WP18 §19. Walks every
 * CONTAINER node in `tree` (dotted path built the same way
 * propertyPathForResult/insertPropertyLeaf already key the tree, e.g.
 * `cust_params`, `cust_params.gpp`) and returns the set of paths that should
 * start expanded. A path is in the set when `nodeNeedsExpand` is true for
 * the subtree rooted there, OR when `opts.forceAll` is set.
 *
 * `forceAll` is WP18's status-pill composition rule: when a RollupRow pill
 * (WP7) has narrowed a family card to one status, `FamilyPropertyCard`
 * passes `forceAll: true` here because at that point `tree` was already
 * built from ONLY the matching-status results — every leaf left in it is a
 * "surviving" leaf the pill selected, so every one of its ancestor
 * containers must be expanded, not just the ones that happen to contain a
 * fail/warn. This is what makes "an active status pill never leaves a
 * matching leaf hidden under a collapsed node" true unconditionally, rather
 * than only for warn/fail pills — see the render layer's use of this flag
 * to also bypass any stale manual per-node collapse.
 *
 * Pure, never throws; garbage input yields an empty set.
 */
export function defaultExpandedPaths(tree, opts = {}) {
  const expanded = new Set();
  const forceAll = !!(opts && opts.forceAll);
  try {
    const walk = (node, prefix) => {
      if (!node || typeof node !== 'object') return;
      for (const key of Object.keys(node)) {
        if (key === TREE_SELF_LEAF_KEY) continue;
        const value = node[key];
        if (!value || typeof value !== 'object') continue;
        if (value[TREE_LEAF_FLAG] === true) continue; // plain leaf, no chevron to default
        const path = prefix ? `${prefix}.${key}` : key;
        if (forceAll || nodeNeedsExpand(value)) expanded.add(path);
        walk(value, path);
      }
    };
    walk(tree, '');
    return expanded;
  } catch {
    return expanded;
  }
}

/**
 * LeafLine — one property-path leaf's row: verdict glyph, dotted key, and
 * `formatLeafRead(leaf)`. Factored out of PropertyTreeNode by WP16 §17b
 * (docs/checks-page-ux-spec.md) so it can render in TWO positions: a plain
 * leaf (a path with no children), and a CONTAINER's own verdict on the
 * container's own line (`node[TREE_SELF_LEAF_KEY]`) — the same visual
 * either way, since to a reviewer both are just "this property, pass or
 * fail, here's the value".
 */
function LeafLine({ label, leaf, depth }) {
  const accent = leaf.ok ? STATUS_ACCENT.pass : STATUS_ACCENT.fail;
  const glyph = leaf.ok ? STATUS_GLYPH.pass : STATUS_GLYPH.fail;
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.25, pl: depth * 2.5 }}>
      <Typography aria-hidden sx={{ width: 18, flexShrink: 0, fontWeight: 700, color: accent }}>
        {glyph}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', flexShrink: 0 }}>
        {label}:
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: 'ui-monospace, Menlo, monospace',
          color: leaf.ok ? 'text.primary' : 'error.main',
          wordBreak: 'break-word',
        }}
      >
        {formatLeafRead(leaf)}
      </Typography>
    </Box>
  );
}

/**
 * PropertyTreeNode — WP15 §16: recursive renderer for one pivoted request's
 * `tree` (pivotResultsByRequest's output, already sorted alphabetically at
 * every level by sortTreeKeysDeep — this component just walks `Object.keys`
 * in the order it receives them). A leaf (`{ __leaf: true, ok, read }`)
 * renders via `LeafLine`; anything else is a subsection header that
 * recurses into indented children. Plain nested `<Box>`/`<Typography>` —
 * not a `<Table>`; see ResultRow's doc comment above for why this page
 * stopped reaching for `<Table>` on content that isn't literally tabular
 * (WP4/WP10/WP13c all collapsed to unreadable slivers that way). Defensive:
 * a non-object node renders nothing.
 *
 * WP16 §17b (docs/checks-page-ux-spec.md) — a path that is BOTH a leaf and
 * a parent (`cust_params` checked whole, plus `cust_params.hb_pb`
 * individually) used to stuff the container's own verdict under a fabricated
 * child key (`'(value)'`), which read as a meaningless property name. That
 * verdict now lives at `node[TREE_SELF_LEAF_KEY]`: rendered here as the
 * CONTAINER's own line (via `LeafLine`, same as any other property), with
 * its children indented beneath — never enumerated as a child row itself.
 */
function PropertyTreeNode({ node, depth = 0 }) {
  if (!node || typeof node !== 'object') return null;
  return (
    <>
      {Object.keys(node).filter((key) => key !== TREE_SELF_LEAF_KEY).map((key) => {
        const value = node[key];
        if (value && typeof value === 'object' && value[TREE_LEAF_FLAG] === true) {
          return <LeafLine key={key} label={key} leaf={value} depth={depth} />;
        }
        const selfLeaf = value && typeof value === 'object' ? value[TREE_SELF_LEAF_KEY] : undefined;
        return (
          <Box key={key}>
            {selfLeaf ? (
              <LeafLine label={key} leaf={selfLeaf} depth={depth} />
            ) : (
              <Box sx={{ pl: depth * 2.5 }}>
                <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700, py: 0.25 }}>
                  {key}:
                </Typography>
              </Box>
            )}
            <PropertyTreeNode node={value} depth={depth + 1} />
          </Box>
        );
      })}
    </>
  );
}

/**
 * AggregateLeafLine — one property's aggregated verdict across the whole
 * family, WP17 §18: glyph + property key + plain-language rule
 * (`leafRuleText`, WP12's `checkLabel`) + tally (`formatAggregateTally`,
 * reusing WP14's `failingRequestsSummary`) + a "view request" link on
 * failure (`firstFailingRequestId` + `requestLink`, WP14), plus a
 * drill-down line (`aggregateFailureDrilldown`). Also doubles as the
 * CROSS-REQUEST block's row shape (label passed in directly there instead
 * of a property key) — see CrossRequestLine below, which renders the same
 * visual family without a "property key : rule" split (a session-level
 * check has no property key).
 */
function AggregateLeafLine({ label, leaf, depth, result, checkDefsById }) {
  const { setJumpTarget } = useTraffic();
  const navigate = useNavigate();
  const def = checkDefsById ? checkDefsById.get(leaf.checkId) : undefined;
  const ruleText = leafRuleText(def, leaf.checkId);
  const ok = leaf.failedCount === 0;
  const accent = ok ? STATUS_ACCENT.pass : (STATUS_ACCENT[leaf.status] || STATUS_ACCENT.fail);
  const glyph = ok ? STATUS_GLYPH.pass : (STATUS_GLYPH[leaf.status] || STATUS_GLYPH.fail);
  const tally = formatAggregateTally(result, leaf);
  const link = !ok && leaf.firstFailingRequestId ? requestLink(leaf.firstFailingRequestId) : null;
  const drilldown = !ok ? aggregateFailureDrilldown(result) : null;
  function viewRequest(e) {
    e.stopPropagation();
    if (!leaf.firstFailingRequestId) return;
    setJumpTarget(leaf.firstFailingRequestId);
    navigate('/', { state: { from: '/checks', fromLabel: 'Checks' } });
  }
  return (
    <Box sx={{ pl: depth * 2.5, py: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Typography aria-hidden sx={{ width: 18, flexShrink: 0, fontWeight: 700, color: accent }}>{glyph}</Typography>
        <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', flexShrink: 0 }}>{label}:</Typography>
        <Typography variant="body2" sx={{ color: ok ? 'text.primary' : 'error.main', wordBreak: 'break-word' }}>{ruleText}</Typography>
        <Box sx={{ flex: 1, minWidth: 8 }} />
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{tally}</Typography>
        {link && (
          <Button
            size="small"
            variant="text"
            onClick={viewRequest}
            sx={{ p: 0, minWidth: 0, fontSize: 'inherit', fontWeight: 600, textTransform: 'none' }}
          >
            → view request
          </Button>
        )}
      </Box>
      {drilldown && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 2.5, wordBreak: 'break-word' }}>
          └ {drilldown}
        </Typography>
      )}
    </Box>
  );
}

/**
 * ContainerEncodingLine — the encoding-integrity sub-line under a container
 * property (cust_params, FreeWheel's KVP params, …), WP17 §18. Renders ONLY
 * when `attachEncodingIntegrity` stamped a verdict onto this container —
 * absent entirely for a container the encoding check never evidenced either
 * way (see that function's doc comment). Names leaked keys (from the
 * `under_encoded_nested_value` message) when the verdict carries any.
 */
function ContainerEncodingLine({ encoding, depth }) {
  if (!encoding) return null;
  const ok = encoding.clean === true;
  const accent = ok ? STATUS_ACCENT.pass : STATUS_ACCENT.warn;
  const glyph = ok ? STATUS_GLYPH.pass : STATUS_GLYPH.warn;
  const codeLabel = encoding.code === 'double_encoded' ? 'double-encoded'
    : encoding.code === 'truncated_value' ? 'truncated'
    : encoding.code === 'unencoded_nested_url' ? 'unencoded'
    : 'under-encoded';
  const text = ok ? 'properly encoded' : `properly encoded — ${codeLabel} on ${encoding.failedCount}`;
  return (
    <Box sx={{ pl: depth * 2.5, py: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography aria-hidden sx={{ width: 18, flexShrink: 0, fontWeight: 700, color: accent }}>{glyph}</Typography>
        <Typography variant="body2" sx={{ color: ok ? 'text.primary' : 'error.main' }}>{text}</Typography>
      </Box>
      {!ok && Array.isArray(encoding.leakedKeys) && encoding.leakedKeys.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 2.5 }}>
          leaked keys: {encoding.leakedKeys.join(', ')}
        </Typography>
      )}
    </Box>
  );
}

/**
 * ContainerToggle — WP18 §19's chevron: a real `<button>` (MUI IconButton)
 * with `aria-expanded` and a visible focus ring, plus the collapsed-state
 * summary line ("cust_params ▸ 4 keys · 1 failing", `containerSummary`) with
 * its own worst-status glyph. Rendered whenever a container is collapsed;
 * the open state instead renders the plain "key: {" line (unchanged from
 * before this work package) alongside this same button.
 */
function ContainerToggle({ label, isOpen, onToggle, summary }) {
  const glyph = STATUS_GLYPH[summary.worst] || STATUS_GLYPH.pass;
  const accent = STATUS_ACCENT[summary.worst] || STATUS_ACCENT.pass;
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
      <IconButton
        size="small"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-label={isOpen ? `Collapse ${label}` : `Expand ${label}`}
        sx={{
          p: 0.25,
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
        }}
      >
        <span aria-hidden style={{ display: 'inline-block', transition: 'transform 0.15s', transform: isOpen ? 'rotate(90deg)' : 'none' }}>
          ▸
        </span>
      </IconButton>
      {isOpen ? (
        <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700, py: 0.25 }}>
          {label}: {'{'}
        </Typography>
      ) : (
        <>
          <Typography aria-hidden sx={{ width: 18, flexShrink: 0, fontWeight: 700, color: accent }}>{glyph}</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', py: 0.25 }}>
            {label} · {summary.childCount} key{summary.childCount === 1 ? '' : 's'}
            {summary.failingCount > 0 ? ` · ${summary.failingCount} failing` : ''}
          </Typography>
        </>
      )}
    </Box>
  );
}

/**
 * PropertyAggregateTreeNode — WP17 §18's family-card body: recursive walk of
 * `aggregateFamilyByProperty`'s (already alpha-sorted) tree. A plain leaf
 * renders via AggregateLeafLine; a container renders its own `__self`
 * leaf/encoding line (when present) then recurses into children — same
 * shape as WP15/WP16's `PropertyTreeNode`, kept as a separate component
 * because the leaf payload differs (aggregate tallies vs. a single request's
 * read/ok) and merging the two shapes into one renderer risked exactly the
 * silent-misattribution failure mode this work package was scoped to avoid.
 *
 * WP18 §19 — containers are now collapsible. `path` is the dotted key path
 * built the SAME way the tree itself is keyed (propertyPathForResult /
 * insertPropertyLeaf), so `overrides`/`defaultExpanded` (both keyed by that
 * same path string, built once in FamilyPropertyCard) line up exactly with
 * what's rendered here. `forceOpen` (the status-pill composition rule) wins
 * over BOTH the computed default AND any manual override — see
 * defaultExpandedPaths' doc comment on why an active pill must never leave
 * a surviving leaf hidden under a collapsed node. The container's own
 * `__self` leaf/encoding line is rendered UNCONDITIONALLY regardless of
 * collapse state (WP18's other non-negotiable: the encoding verdict stays
 * visible either way) — only the deeper children + closing brace are gated
 * behind `isOpen`.
 *
 * Defensive: non-object node renders nothing.
 */
function PropertyAggregateTreeNode({ node, depth = 0, path = '', resultsByCheckId, checkDefsById, overrides, defaultExpanded, onToggle, forceOpen = false }) {
  if (!node || typeof node !== 'object') return null;
  return (
    <>
      {Object.keys(node).filter((key) => key !== TREE_SELF_LEAF_KEY).map((key) => {
        const value = node[key];
        const childPath = path ? `${path}.${key}` : key;
        if (value && value[TREE_LEAF_FLAG] === true) {
          const result = resultsByCheckId ? resultsByCheckId.get(value.checkId) : undefined;
          return <AggregateLeafLine key={key} label={key} leaf={value} depth={depth} result={result} checkDefsById={checkDefsById} />;
        }
        const selfLeaf = value ? value[TREE_SELF_LEAF_KEY] : null;
        const hasSelfCheck = !!(selfLeaf && selfLeaf[TREE_LEAF_FLAG] === true);
        const selfResult = hasSelfCheck && resultsByCheckId ? resultsByCheckId.get(selfLeaf.checkId) : undefined;
        const isOpen = forceOpen
          || (overrides && overrides.has(childPath) ? overrides.get(childPath) : !!(defaultExpanded && defaultExpanded.has(childPath)));
        const summary = containerSummary(value);
        return (
          <Box key={key}>
            {hasSelfCheck ? (
              // A container that is ALSO a check's own subject (WP16 §17b
              // collision) keeps its verdict on its own line always — same
              // as the encoding line below, this is a "container's own
              // truth" line, not a child, so collapse never touches it.
              <AggregateLeafLine label={key} leaf={selfLeaf} depth={depth} result={selfResult} checkDefsById={checkDefsById} />
            ) : (
              <Box sx={{ pl: depth * 2.5 }}>
                <ContainerToggle label={key} isOpen={isOpen} onToggle={() => onToggle && onToggle(childPath)} summary={summary} />
              </Box>
            )}
            {selfLeaf && selfLeaf.encoding && <ContainerEncodingLine encoding={selfLeaf.encoding} depth={depth + 1} />}
            {isOpen && (
              <PropertyAggregateTreeNode
                node={value}
                depth={depth + 1}
                path={childPath}
                resultsByCheckId={resultsByCheckId}
                checkDefsById={checkDefsById}
                overrides={overrides}
                defaultExpanded={defaultExpanded}
                onToggle={onToggle}
                forceOpen={forceOpen}
              />
            )}
            {isOpen && !hasSelfCheck && (
              <Box sx={{ pl: depth * 2.5 }}>
                <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', color: 'text.secondary', py: 0.25 }}>
                  {'}'}
                </Typography>
              </Box>
            )}
          </Box>
        );
      })}
    </>
  );
}

/** Build a { pass, warn, fail, info, 'n/a' } rollup from a results[] slice — client-side
 * fallback for LayeredResults below when the server didn't supply a rollupByLayer. */
function rollupOf(results) {
  // WP1: a failing 'recommend'-severity check resolves to status 'recommend'
  // (evaluateCheck.js) — tallied here, never folded into fail/warn/pass.
  const rollup = { pass: 0, warn: 0, fail: 0, info: 0, 'n/a': 0, recommend: 0 };
  for (const r of results || []) {
    const s = r?.status;
    if (s && rollup[s] !== undefined) rollup[s] += 1;
  }
  return rollup;
}

/**
 * familyOfResult(result) -> family key (WP2, docs/checks-page-ux-spec.md §4)
 *
 * A baseline/run result never echoes `target` back (evaluateCheck.js's
 * contract — see vendorFamilies.js's doc comment on checkMatchesFamily), so
 * unlike checkMatchesFamily's target.requestType path this is purely
 * empirical: it reads the SAME evidence arrays (`result.rows[]`, and
 * `result.evidence[].row` for shapes that carry that instead) and buckets by
 * `familyOfRequestType` (vendorFamilies.js — no second taxonomy). A result
 * whose evidence spans more than one family (rare — most checks target one
 * request type) is assigned its DOMINANT family by row count, ties broken by
 * first-seen order, rather than being split across sections. A result with
 * no attributable evidence falls into 'other' — grouping must place every
 * result exactly once (WP2 acceptance: "all checks reachable"). Never throws.
 */
export function familyOfResult(result) {
  try {
    if (!result || typeof result !== 'object') return 'other';
    const evidenceRows = [];
    if (Array.isArray(result.rows)) evidenceRows.push(...result.rows);
    if (Array.isArray(result.evidence)) {
      for (const e of result.evidence) if (e && e.row) evidenceRows.push(e.row);
    }
    if (evidenceRows.length === 0) return 'other';
    const counts = new Map();
    for (const row of evidenceRows) {
      const fam = familyOfRequestType(row?.requestType) || 'other';
      counts.set(fam, (counts.get(fam) || 0) + 1);
    }
    let best = 'other';
    let bestCount = -1;
    for (const [fam, count] of counts) {
      if (count > bestCount) { best = fam; bestCount = count; }
    }
    return best;
  } catch {
    return 'other';
  }
}

/**
 * groupResultsByFamily(results) -> [{ family, label, results, rollup }]
 * (WP2, docs/checks-page-ux-spec.md §4)
 *
 * Partitions a flat results[] into one bucket per vendor family (via
 * familyOfResult above) — every result lands in exactly one group, so
 * summing each group's `rollup` reproduces `rollupOf(results)` exactly
 * (tested in ChecksPage.test.js). Sorted by result count descending, same
 * convention as vendorFamilies.js's tallyFamilies (no special-casing
 * 'other' — consistency over cleverness). Never throws; malformed input
 * yields [].
 */
export function groupResultsByFamily(results) {
  try {
    const list = Array.isArray(results) ? results : [];
    const byFamily = new Map(); // family -> results[]
    for (const r of list) {
      const fam = familyOfResult(r);
      if (!byFamily.has(fam)) byFamily.set(fam, []);
      byFamily.get(fam).push(r);
    }
    return Array.from(byFamily.entries())
      .map(([family, groupResults]) => ({
        family,
        label: FAMILY_LABELS[family] || family,
        results: groupResults,
        rollup: rollupOf(groupResults),
      }))
      .sort((a, b) => b.results.length - a.results.length);
  } catch {
    return [];
  }
}

/**
 * filterResultsByFamily(results, family) -> results[] (WP1, docs/checks-page-ux-spec.md §3)
 *
 * Pure extraction of the baseline section's family-chip VIEW filter — never
 * touches the run itself (the caller passes the full-run `results`
 * unmodified; this only narrows what's DISPLAYED). Two guarantees this is
 * tested against:
 *
 *   1. ALL_FAMILY (or falsy) returns the SAME array reference, unmodified —
 *      the byte-identical-to-today invariant. No result is cloned, no
 *      `viewMeta` is attached, so every downstream renderer takes the exact
 *      code path it did before WP1.
 *   2. Otherwise, keeps only results checkMatchesFamily() attributes to
 *      `family` (unchanged pre-WP1 behavior), narrows each survivor's
 *      `rows[]` to that family (also unchanged), and additionally stamps a
 *      `viewMeta` object — `{ family, familyLabel, totalRows, viewRows,
 *      viewFound }` — recording what the PRE-filter rows[] looked like
 *      alongside the post-filter counts. `matched`/`found`/`scopedTotal`
 *      on the result are left untouched (still full-run truth); `viewMeta`
 *      is the second fact the UI now renders alongside them so a viewer
 *      never sees a number without knowing which scope it describes.
 */
export function filterResultsByFamily(results, family) {
  const list = Array.isArray(results) ? results : [];
  if (!family || family === ALL_FAMILY) return list;
  return list
    .filter((r) => checkMatchesFamily(r, family))
    .map((r) => {
      if (!r || !Array.isArray(r.rows)) return r;
      const totalRows = r.rows.length;
      const viewRows = r.rows.filter((row) => familyOfRequestType(row?.requestType) === family);
      return {
        ...r,
        rows: viewRows,
        viewMeta: {
          family,
          familyLabel: FAMILY_LABELS[family] || family,
          totalRows,
          viewRows: viewRows.length,
          viewFound: viewRows.filter((row) => row?.ok === true).length,
        },
      };
    });
}

/**
 * WP3 (docs/checks-page-ux-spec.md §5) — "Tool Driven" and "Manual" were the
 * same author→compile→run box (AutoPanel) with only the Driven affordance
 * differing; collapsed into one `author` mode. `build` is the separate
 * work-order flow (BuildValidationPanel). This is the full, exhaustive set.
 *
 * `signoff` and `rules` were the standalone Launch and Rules pages (retired
 * 2026-10-07 — three nav entries for one feature read as three tools). They
 * render the same components here; /launch and /rules redirect to
 * /checks?mode=signoff|rules so old links land on the right tab.
 *
 * `build` was folded into Sign off the same day: driving a device is a way to
 * PRODUCE the traffic a launch is graded on, so BuildValidationPanel is now the
 * "Drive" option of a launch's Verify card. An old ?mode=build link opens Sign off.
 */
export const CHECKS_MODES = ['agent', 'signoff', 'rules', 'iu'];

// iu = IU Finder (docs/iu-finder-spec.md), formerly its own /iu-finder page.
const CHECKS_MODE_LABELS = { agent: 'Agent', signoff: 'Sign off', rules: 'Rules', iu: 'IU Finder' };
const MODE_ALIASES = { build: 'signoff' };

/** modeFromSearch(search) — the ?mode= deep link, or 'agent' when absent/unknown. */
export function modeFromSearch(search) {
  try {
    const raw = new URLSearchParams(search || '').get('mode');
    const m = MODE_ALIASES[raw] || raw;
    return CHECKS_MODES.includes(m) ? m : 'agent';
  } catch { return 'agent'; }
}

/**
 * rowsToPost(hasScopedHandoff, scopedRows) — what a Checks action should POST as
 * `rows`. Only an EXPLICIT Dashboard selection travels; otherwise undefined so
 * the proxy resolves the session from its own history (no multi-MB upload).
 */
export function rowsToPost(hasScopedHandoff, scopedRows) {
  return hasScopedHandoff && Array.isArray(scopedRows) && scopedRows.length ? scopedRows : undefined;
}

/** App Pack state shown beside the baseline run (no silent default). */
export function appPackStateLabel(selectedApp) {
  return selectedApp
    ? `App Pack: ${selectedApp}`
    : 'No App Pack selected \u2014 business-unit rules are off (only the generic baseline runs).';
}

// WP5 (docs/checks-page-ux-spec.md §7) — the notice shown AT THE AUTHOR
// SURFACE when Build's free-text box classified a submission as a question
// (`kind: 'question'`) rather than a build request, and handed it up via
// `onAskQuestion`. Named once here so BuildValidationPanel's own fine print
// (which explains the same redirect in advance) can't drift from what the
// notice actually says.
export const REDIRECT_TO_AUTHOR_NOTICE =
  "That read as a question rather than a build request, so it's answered here. Your text is prefilled.";

/**
 * buildAskQuestionRedirect(questionText) — pure transition for
 * BuildValidationPanel's `onAskQuestion(q)` callback: what mode to land on,
 * what to prefill the Author box with, and what notice to show there. Kept
 * pure (no state writes) so the redirect decision is unit-testable without
 * rendering anything.
 */
export function buildAskQuestionRedirect(questionText) {
  return {
    // Was 'author'. Author is gone and its ask box moved into AgentPanel, so a
    // question asked in Build lands on Agent — the one surface that answers.
    mode: 'agent',
    prefillAuthor: typeof questionText === 'string' ? questionText : '',
    notice: REDIRECT_TO_AUTHOR_NOTICE,
  };
}

/**
 * computeDriveAvailability(driver, drivenPlatform) — WP3 (docs/checks-page-ux-spec.md
 * §5): pure extraction of AutoPanel's drive-toggle self-disable logic, so
 * "the toggle is disabled (not absent) with no device" is a testable fact
 * rather than something only visible by rendering. `driver` is the shape the
 * proxy hands back for the selected app (`{ drivable, webDrivable, reason,
 * webReason }`) or null/undefined while still resolving.
 *
 * - `isWebDrive` — whether the currently-selected drive platform is 'web'
 *   (web drives by URL, not by a picked app).
 * - `canDrive` — whether the toggle itself may be turned on. Optimistic
 *   (true) while `driver` hasn't resolved yet; once resolved, gated on the
 *   platform-appropriate flag.
 * - `driverAllUnavailable` — `driver` has resolved AND every platform came
 *   back unavailable, i.e. there is nothing to offer at all (not merely "this
 *   one platform"). Callers collapse the whole toggle/segmented-control stack
 *   to one muted caption in this case instead of a disabled control.
 */
export function computeDriveAvailability(driver, drivenPlatform) {
  const isWebDrive = drivenPlatform === 'web';
  const canDrive = driver ? (isWebDrive ? !!driver.webDrivable : !!driver.drivable) : true;
  const driverAllUnavailable = !!driver && !driver.drivable && !driver.webDrivable;
  return { isWebDrive, canDrive, driverAllUnavailable };
}

// Debounce window for the token-estimate readout — rapid scope changes
// (arriving from a fresh Dashboard handoff) shouldn't spam the proxy.
const ESTIMATE_DEBOUNCE_MS = 400;

/** Compact token count, e.g. 1234 -> "1.2k", 950 -> "950", 15000 -> "15.0k". */
function formatTokenCount(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return null;
  if (v < 1000) return String(Math.round(v));
  return `${(v / 1000).toFixed(1)}k`;
}

/**
 * ScopeBanner — compact replacement for the old in-page request picker.
 * The actual selection now happens on the Dashboard (InspectraNetworkView's
 * `picked` row-select → "Run checks on selected" → TrafficContext.checksScopeRows).
 * This banner just reports the resulting scope and estimates its LLM-input
 * token cost (discover/propose/audit only — run is free). Defensive: a
 * missing/empty scope never throws, it just reports "using all captured".
 */
function ScopeBanner({ scopedRows, hasScopedHandoff, totalCount, selectedApp, onClear, adOnly = false, adTraffic = null, filteredCount = null }) {
  const [estimate, setEstimate] = useState(null); // { profileTokens, rowCount, note } or null
  const [estimating, setEstimating] = useState(false);

  const rows = useMemo(() => (Array.isArray(scopedRows) ? scopedRows : []), [scopedRows]);

  // Debounced, stale-response-guarded token estimate — recomputed whenever the
  // resolved scope (or the adOnly filter) changes. Never throws; on any failure
  // the readout just hides.
  useEffect(() => {
    let cancelled = false;
    setEstimating(true);
    const timer = setTimeout(() => {
      apiEstimateScope({
        app: selectedApp || undefined,
        // Only an explicit selection travels — without it the route estimates
        // over the proxy's own history, which IS this session. See `postRows`
        // on the page for why re-uploading it is what makes the proxy flap.
        rows: (hasScopedHandoff && rows.length) ? rows : undefined,
        ...(adOnly ? { adOnly: true } : {}),
      })
        .then((res) => {
          if (cancelled) return;
          setEstimate(res && typeof res.profileTokens === 'number' ? res : null);
        })
        .catch(() => {
          if (cancelled) return;
          setEstimate(null);
        })
        .finally(() => {
          if (cancelled) return;
          setEstimating(false);
        });
    }, ESTIMATE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, selectedApp, adOnly, hasScopedHandoff]);

  const tokenLabel = formatTokenCount(estimate?.profileTokens);
  const total = Number.isFinite(Number(totalCount)) ? Number(totalCount) : 0;
  const fullCount = hasScopedHandoff ? rows.length : total;
  const at = adTraffic && typeof adTraffic === 'object' ? adTraffic : null;
  const filtered = Number.isFinite(Number(filteredCount)) ? Number(filteredCount) : null;

  // WP1 (docs/checks-adscope-spec.md §4) — the honest "N ad-relevant of M"
  // count. classifyRows() (proxy adTraffic/classifyAdTraffic.js) only rolls
  // up three of its buckets into `adRows`/`nonAdRows`/`unknownRows`: AD_ISH
  // categories -> adRows, 'non_ad' -> nonAdRows, 'unknown' -> unknownRows.
  // 'consent' (and 'analytics') rows are tracked ONLY in `byCategory`, not
  // in any of those three counters. But the proxy's actual adOnly scope
  // filter (isAdRelevant/AD_RELEVANT in the same file) KEEPS consent rows
  // (they're part of the ad-request handshake) and drops only non_ad +
  // analytics. So `at.adRows` alone undercounts what adOnly will actually
  // keep — pull the consent bucket out of `byCategory` and add it in so the
  // displayed N matches AD_ISH + unknown + consent, exactly what scopeRows()
  // keeps server-side.
  const consentCount = at && Array.isArray(at.byCategory)
    ? (at.byCategory.find((c) => c && c.category === 'consent')?.count || 0)
    : 0;
  const adRelevantCount = at ? (Number(at.adRows) || 0) + (Number(at.unknownRows) || 0) + consentCount : null;
  // introspect's echoed `rowCount` (filteredCount) is the proxy's own
  // post-scopeRows() row count for this same call — it and adRelevantCount
  // come from the same response and should always agree; keep it as a
  // fallback readout for the brief window before `adTraffic` has loaded.
  const displayN = adRelevantCount != null ? adRelevantCount : filtered;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {adOnly ? (
          <>
            <Chip size="small" color="primary" label="Ad traffic only" sx={{ fontWeight: 700 }} />
            <Typography variant="body2" color="text.secondary">
              {displayN != null
                ? `Validating ${displayN} ad-relevant of ${fullCount} captured requests`
                : `Scope: ad traffic only (${fullCount} requests)`}
              {hasScopedHandoff ? ' · selected on the Dashboard' : ''}.
            </Typography>
            {hasScopedHandoff && <Button size="small" onClick={onClear}>Clear</Button>}
          </>
        ) : hasScopedHandoff ? (
          <>
            <Chip size="small" color="primary" label={`Scoped: ${rows.length} request${rows.length === 1 ? '' : 's'}`} sx={{ fontWeight: 700 }} />
            <Typography variant="body2" color="text.secondary">
              Selected on the Dashboard.
            </Typography>
            <Button size="small" onClick={onClear}>Clear</Button>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Using all captured requests ({total}) — select requests on the Dashboard to scope.
          </Typography>
        )}
        {at && (
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={`Ads ${Number(at.adRows) || 0}`} />
            <Chip size="small" variant="outlined" label={`Non-ad ${Number(at.nonAdRows) || 0}`} />
            <Chip size="small" variant="outlined" label={`Unknown ${Number(at.unknownRows) || 0}`} />
          </Stack>
        )}
        {at && Number(at.adRows) === 0 && fullCount > 0 && (
          <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
            ⚠️ No ad traffic in this scope — most checks will return n/a. Capture ad activity or widen the scope.
          </Typography>
        )}
        {tokenLabel && (
          <Tooltip title={estimate?.note || ''} arrow>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              ~{tokenLabel} tokens LLM input (discover/propose) · run is free
            </Typography>
          </Tooltip>
        )}
        {estimating && !tokenLabel && (
          <CircularProgress size={12} thickness={5} />
        )}
      </Stack>
    </Paper>
  );
}

// Debounce window for the introspect readout — mirrors ESTIMATE_DEBOUNCE_MS,
// recomputed whenever the resolved scope changes while the panel is open.
const INTROSPECT_DEBOUNCE_MS = 400;

// HH:MM:SS from a sample's `ts` — tolerant of an ISO string or a ms number
// (mirrors MainApp.js's ts() tolerance). Never throws; '' on anything unparsable.
function formatSampleTime(ts) {
  try {
    const n = typeof ts === 'number' ? ts : Date.parse(ts);
    if (!Number.isFinite(n)) return '';
    const d = new Date(n);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-US', { hour12: false });
  } catch {
    return '';
  }
}

/**
 * Small, fast, deterministic string hash (FNV-1a). Not cryptographic — only
 * used to keep a fingerprint string short. A collision would merely cause an
 * occasional unnecessary re-run, never a wrong restore: the fingerprint also
 * carries the plain row count alongside the hash, and the two would both
 * have to coincide with an unrelated scope for a bad restore to happen.
 */
export function hashString(str) {
  const s = typeof str === 'string' ? str : String(str ?? '');
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/**
 * computeBaselineFingerprint({ rows, app, adOnly, suite }) — WP11 (§12).
 *
 * Fingerprints everything the baseline auto-run's own inputs are: which rows
 * it scoped over (identity, not just count — a same-size scope after a
 * session clear/replay is a DIFFERENT scope), the selected app, the adOnly
 * toggle, and the suite name. Built from each row's `requestId` (falling
 * back to `id`, then its index) rather than full row content — cheap, and
 * requestId identity is exactly what every other row-matching path in this
 * file already treats as a row's identity (rowsByRequestId, jumpToSample,
 * jumpToEvidenceRow, etc).
 *
 * A mismatch on ANY field (row identity, app, adOnly, or suite) means "not
 * the same run" — the caller must treat it as a full miss and let the normal
 * auto-run effect fire, never partially reuse a stored report. Pure, never
 * throws.
 */
export function computeBaselineFingerprint({ rows, app, adOnly, suite } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const ids = list.map((r, i) => r?.requestId || r?.id || `#${i}`).join('|');
  return `n=${list.length};h=${hashString(ids)};app=${app || ''};adOnly=${adOnly ? 1 : 0};suite=${suite || ''}`;
}

/**
 * slimBaselineReport(report) — WP11 (§12).
 *
 * Mirrors the Ask-thread's sessionStorage strip (slimAskTurn /
 * ASK_THREAD_STORE_KEY, components/AgentPanel.jsx): sessionStorage has a
 * ~5MB budget, and an
 * unrecorded baseline report over a real session (~731 rows, ~150 checks) is
 * larger than an Ask thread ever is. The unbounded part is each result's
 * `rows[]` — one evidence entry per matched row, per check — so that's the
 * only thing stripped. `failures` (small), `patterns`/`failureSamples`
 * (server-capped ≤10 — see FailureForensics above) and `session` (a handful
 * of numbers) all stay, so a restored report still renders the rollup, scope
 * notes, and FailureForensics correctly — only EvidenceRowsTable's full
 * per-row drill-down is unavailable on a restored result (it degrades to its
 * existing "no rows" empty state, same as any result with no evidence rows)
 * until the next real run repopulates it. Pure, never throws; returns null
 * for a non-object input.
 */
export function slimBaselineReport(report) {
  if (!report || typeof report !== 'object') return null;
  const results = Array.isArray(report.results)
    ? report.results.map((r) => {
      if (!r || typeof r !== 'object') return r;
      const { rows: _rows, ...rest } = r;
      return rest;
    })
    : report.results;
  return { ...report, results };
}

export default function ChecksPage() {
  const { liveTrafficGroups, checksScopeRows, setChecksScopeRows } = useTraffic();

  // ── Author vs Build — mutually exclusive, one card (see render below).
  //    WP3 (docs/checks-page-ux-spec.md §5) collapsed the old "Tool Driven"
  //    and "Manual" tabs into one `author` mode: the same ad-hoc NL
  //    author→compile→run box (AutoPanel) that can EITHER drive the
  //    simulator/install a build and validate against freshly driven traffic
  //    OR work passively over the requests selected on the Dashboard (surface
  //    what's in them, author a one-off check, generate an AI check set to
  //    review/run) — driving is now a toggle inside that one surface, not a
  //    separate mode. `build` is BuildValidationPanel's own work-order flow.
  //    CHECKS_MODES (above) is the exhaustive set: {author, build}. ──
  const [prefillAuthor] = useState(() => {
    try {
      const p = sessionStorage.getItem('checks.prefillAuthor');
      if (p) sessionStorage.removeItem('checks.prefillAuthor');
      return p || '';
    } catch { return ''; }
  });
  // WP5 (§7) — set alongside prefillAuthor when Build redirects a question
  // here; rendered as an inline notice inside AutoPanel and cleared on
  // resubmit/edit (see AutoPanel's handleAuto / TextField onChange) so it
  // can't linger into a later, unrelated visit.
  const [redirectNotice, setRedirectNotice] = useState(null);
  const location = useLocation();
  const [mode, setMode] = useState(() => modeFromSearch(location.search)); // CHECKS_MODES
  // A nav to /checks?mode=… while already mounted (e.g. the /launch redirect
  // from another Checks tab) must switch the tab, not just the URL.
  useEffect(() => {
    if (new URLSearchParams(location.search).has('mode')) setMode(modeFromSearch(location.search));
  }, [location.search]);

  // Device/simulator connection status — fetched ONCE here (not inside
  // AutoPanel) since it's proxy-level state relevant to the author surface's
  // Driven toggle regardless of anything else on the page.
  const [driver, setDriver] = useState(null); // { drivable, reason, apps, chromium, webDrivable, webReason }
  useEffect(() => {
    let alive = true;
    apiDriverStatus().then((d) => { if (alive) setDriver(d); }).catch(() => { if (alive) setDriver({ drivable: false, reason: 'Could not reach the proxy to check device support.', webDrivable: false, webReason: 'Could not reach the proxy to check web support.' }); });
    return () => { alive = false; };
  }, []);

  // ── App ─────────────────────────────────────────────────────────────────────
  const [selectedApp] = useState(() => getActiveSelection()?.appId || '');

  // ── Ad-traffic-only scope (persisted) ──────────────────────────────────────
  // WP1 (docs/checks-adscope-spec.md §4) — default ON: every checks API call
  // the page makes carries adOnly:true so the proxy filters resolved rows to
  // ad-relevant categories server-side (no client-side row filtering). The
  // only persisted override is an explicit user toggle-off (localStorage
  // 'checks.adOnly' === 'false'); absent or any other value resolves to ON.
  // Read once on mount — see utils/adOnlyScope.js for the (unit-tested) pure
  // resolve/read/write logic. No UI toggles it any more.
  const [adOnly] = useState(() => readAdOnlyPreference());
  useEffect(() => {
    writeAdOnlyPreference(adOnly);
  }, [adOnly]);

  const sessionRows = useMemo(
    () => Object.values(liveTrafficGroups || {}).flat(),
    [liveTrafficGroups]
  );

  // ── Request scope ─────────────────────────────────────────────────────────
  // Handed off from the Dashboard's row-picker (InspectraNetworkView's `picked`
  // selection → TrafficContext.checksScopeRows), snapshotted at click-time.
  // Non-empty => that IS the scope for Audit/Run/Discover/Suggest. Empty =>
  // falls back to every captured request (unchanged legacy behavior).
  const hasScopedHandoff = Array.isArray(checksScopeRows) && checksScopeRows.length > 0;

  /** Rows to send with the next check action, honoring the Dashboard handoff. */
  const resolveRows = useCallback(() => {
    if (hasScopedHandoff) return checksScopeRows;
    return sessionRows;
  }, [hasScopedHandoff, checksScopeRows, sessionRows]);

  const scopedRows = useMemo(() => resolveRows(), [resolveRows]);

  /**
   * Rows to POST — and usually that is NOTHING.
   *
   * Every /proxy/checks/* route resolves `rows` from the request body IF present
   * and otherwise from the proxy's OWN live history, which is the same session
   * the dashboard is showing. So when the scope is "the whole session" there is
   * nothing to send: the server already has every row.
   *
   * Uploading it anyway is not free. A 1,500-request session is ~9 MB of bodies,
   * re-sent on every scope change by the debounced introspect/estimate effects
   * and again by each Agent action — each one buffered as a string and JSON
   * .parsed on a 1 GB droplet, which is how the proxy ends up flapping and the
   * page fills with ERR_FAILED that reads like a CORS problem.
   *
   * Only an EXPLICIT Dashboard row selection has to travel, because the proxy
   * cannot know which rows the user picked.
   */
  const postRows = useMemo(
    () => rowsToPost(hasScopedHandoff, scopedRows),
    [hasScopedHandoff, scopedRows],
  );

  // Distinct requestType(s) of the Dashboard row-pick — sent to the NL→check
  // compiler as an AUTHORITATIVE scope directive (buildPrompt) so a vague
  // prompt over, say, a Prebid-only selection doesn't get compiled into a
  // Google/GAM check that matches 0 of those rows. Only meaningful when the
  // scope IS an explicit handoff — a full-session scope isn't a type
  // constraint, so this stays undefined then (compiler falls back to its
  // existing broaden-to-family behavior).
  // Page-level introspect — one deterministic, free call (no LLM/key gate) shared
  // by BOTH the ScopeBanner's ad-traffic chips and the "What can I check?" guidance
  // panel (AutoPanel → AuthorGuidancePanel). Debounced + stale-response-guarded,
  // re-run whenever the resolved scope or the adOnly filter changes. Never throws.
  const [introspect, setIntrospect] = useState({ inventory: null, loading: false, error: false });
  useEffect(() => {
    let cancelled = false;
    setIntrospect((s) => ({ ...s, loading: true, error: false }));
    const timer = setTimeout(() => {
      apiIntrospectScope({
        app: selectedApp || undefined,
        rows: postRows,
        ...(adOnly ? { adOnly: true } : {}),
      })
        .then((res) => {
          if (cancelled) return;
          setIntrospect({ inventory: res && typeof res === 'object' ? res : null, loading: false, error: false });
        })
        .catch(() => {
          if (cancelled) return;
          setIntrospect({ inventory: null, loading: false, error: true });
        });
    }, INTROSPECT_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postRows, scopedRows.length, selectedApp, adOnly]);
  const introspectAdTraffic = introspect.inventory?.adTraffic || null;

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Checks</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Describe a check in plain English — the tool compiles it and runs it against captured
        traffic. Sign off grades a feature's acceptance criteria (and can drive a device to
        produce the traffic); Rules is where checks are added or switched off.
      </Typography>

      {/* ── Observed SDK/environment versions — session-level fact, lifted
           intact from the retired TimelinePage (docs: see SdkVersionsPanel.jsx
           header for why it has no other home). Scans the same session rows
           the scope banner below is about to describe. ── */}
      <SdkVersionsPanel rows={sessionRows} />

      {/* ── Request scope — handoff from the Dashboard's row picker ── */}
      <ScopeBanner
        scopedRows={scopedRows}
        hasScopedHandoff={hasScopedHandoff}
        totalCount={sessionRows.length}
        selectedApp={selectedApp}
        onClear={() => setChecksScopeRows([])}
        adOnly={adOnly}
        adTraffic={introspectAdTraffic}
        filteredCount={introspect.inventory?.rowCount}
      />

      {/* ── One card, either/or: "Author" (the ad-hoc NL author→compile→run box
           — can EITHER drive the simulator/install a build and validate
           against freshly driven traffic, via the Driven toggle inside it,
           OR work passively over the Dashboard-selected requests: surfaces
           what's in them, authors one-off checks, generates an AI check set
           to review/run) or "Build" (BuildValidationPanel's work-order flow) —
           never both at once. WP3 (docs/checks-page-ux-spec.md §5) collapsed
           the old "Tool Driven"/"Manual" split into this single Author mode;
           CHECKS_MODES is the exhaustive {author, build} set. App selector +
           device/simulator connection status are shared chrome above the mode
           content since they're relevant to Author either way (driving or
           not). ── */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <ToggleButtonGroup
            size="small"
            value={mode}
            exclusive
            onChange={(e, val) => { if (val) setMode(val); }}
          >
            {CHECKS_MODES.map((m) => (
              <ToggleButton key={m} value={m}>{CHECKS_MODE_LABELS[m]}</ToggleButton>
            ))}
          </ToggleButtonGroup>

        </Stack>

        {mode === 'signoff' ? (
          /* Feature sign-off: acceptance criteria → mapped checks → a verdict
             per criterion. Formerly the Launch page. */
          <LaunchPage embedded />
        ) : mode === 'rules' ? (
          /* Which rules exist and which are switched off — the edit surface
             the Agent tab's read-only rules list points at. Formerly /rules. */
          <RulesPage embedded />
        ) : mode === 'iu' ? (
          /* Which ad unit does a page use — saved HARs (window or picked) + live.
             Free, no LLM. Formerly /iu-finder. */
          <IuFinderPage embedded />
        ) : (
          /* Agent — the consolidated front door: Compile (run the committed
             baseline over this session, free) / Ask (answer from those results
             when possible, escalate to the model only when not) / Rules (the
             suite, which IS the adtech validation ruleset). Replaces the old
             Compile + Propose suite + Discover signals + Audit quartet. */
          <>
          <Typography
            variant="caption"
            data-testid="checks-app-pack-state"
            sx={{ display: 'block', mb: 1, color: selectedApp ? 'var(--muted)' : 'var(--warn-text)' }}
          >
            {appPackStateLabel(selectedApp)}
          </Typography>
          <AgentPanel
            rows={postRows} app={selectedApp} driver={driver}
            initialQuestion={prefillAuthor} redirectNotice={redirectNotice}
            onDismissRedirectNotice={() => setRedirectNotice(null)}
          />
          </>
        )}
      </Paper>

    </Box>
  );
}
